import { Component, DOCUMENT, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/language.service';
import { AddOwnerPartnerComponent } from './add-owner-partner/add-owner-partner.component';
import { OptionTranslateService } from '../../../../shared/services/option-translate.service';
import { MembershipOwnerComponent } from './membership-owner/membership-owner.component';

import { customValidator } from '../../../../Utils/validator';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { SHARED_COMPONENTS } from '../../../../shared/shared-imports';
import { Utils } from '../../../../Utils/utils';
import { ApiService } from '../../../../core/services/api.service';
import { LookupKey } from '../../../../shared/constants/lookup-keys';
import { ApiEndPoints, ApiRoot } from '../../../../shared/constants/api-endpoints';
import { DcciLicenseResponse, LicenseRequestPayload } from './models/dcc-license.model';
import { DocumentServiceType, FieldValue, ServiceName, ServiceNameEn, SrActionType, SrIOName, UserType } from '../../../../shared/constants/services.enum';
import { AddActivityComponent } from "./add-activity/add-activity.component";
import { SharedService } from '../../../../shared/services/shared.service';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { LovSelectionService } from '../../../../core/services/lov-selection.service';
import { RoutesPath } from '../../../../shared/constants/routes-path';
import { SrStatus } from '../../../../shared/constants/status.enum';
import { TextFieldComponent } from '../../../../shared/components/controls/text-field/text-field.component';
import { OptionFieldComponent } from '../../../../shared/components/controls/option-field/option-field.component';
import { SrSubmitSuccessModalComponent } from "../../../../shared/components/sr-submit-success-modal/sr-submit-success-modal.component";
import { AllowedDocumentType, FolderResponse } from '../../../../shared/models/documents-response.model';
import { FileComponent } from '../../../../shared/components/controls/file/file.component';
import { SkeletonLoaderComponent } from "../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { PendingSrModalComponent } from "../../../../shared/components/pending-sr-modal/pending-sr-modal.component";
import { DateComponent } from '../../../../shared/components/controls/date/date.component';
import { MobileNumberComponent } from '../../../../shared/components/controls/mobile-number/mobile-number.component';
import { MembershipSharedService } from './membership-shared-service';
import { LookupService } from '../../../../core/services/lookup.service';

@Component({
  selector: 'app-membership',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    AddOwnerPartnerComponent,
    MembershipOwnerComponent,
    ...SHARED_COMPONENTS,
    AddActivityComponent,
    SrSubmitSuccessModalComponent,
    SkeletonLoaderComponent,
    PendingSrModalComponent
  ],
  templateUrl: './membership.component.html',
  styleUrl: './membership.component.css',
})
export class MembershipComponent implements OnInit, CanComponentDeactivate, OnDestroy {
  @ViewChildren(TextFieldComponent) textFields!: QueryList<TextFieldComponent>;
  @ViewChildren(OptionFieldComponent) optionFields!: QueryList<OptionFieldComponent>;
  @ViewChildren(DateComponent) dateFields!: QueryList<DateComponent>;
  @ViewChildren(MobileNumberComponent) mobileFields!: QueryList<MobileNumberComponent>;
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;

  type: 'registration' | 'cancellation' | 'amendment' | 'renew' = 'registration';
  documentServiceType = DocumentServiceType;
  fieldValue = FieldValue;
  allFields: any = [];
  srNumber: string = "";
  totalSteps: number = 6;
  step = 1;
  lookupKey: any = LookupKey;
  service: any = ServiceName
  form: FormGroup;
  lang: any = '';
  addOwnerModal: boolean = false;
  membershipYears: any = [];
  isOwnerAdded: boolean = false;
  ownerList: any = []
  licenseMember: any = "";
  memberLicenseError: boolean = false;
  loaderValidateMemLicense: boolean = false;
  addActivityModal: boolean = false;
  selectedLicenseAuthority: any = "";
  srAccountId: any = "";
  showActions: boolean = true;
  isSaved: boolean = false;
  successModal: boolean = false;
  activities: any = [];
  selectedActivities: any = [];
  filesList: any = [];
  userCSN: string = "";
  srSubType: string = "";
  userType: string = "";
  licenseCategory: string = "";
  srDetailLoading: boolean = false;
  noAdvancePaymentBalance: boolean = false;
  srInprgoressAwating: boolean = false;
  showNotes: boolean = false;
  isOffshoreLicenseType: boolean = false;
  isSrFromQueryParams: boolean = false;
  initialTradeLicenseValues: { issuingAuthority: string, licenceType: string, licenceNumber: string } | null = null;
  currentSrStatus: string = SrStatus.Draft;
  
  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private optionTranslateService: OptionTranslateService,
    private router: Router,
    private api: ApiService,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private lovService: LovSelectionService,
    private lookupService: LookupService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.userType = this.sharedService.getPrimaryAccount()['Account Type Code'];
    //get route data
    this.route.data.subscribe(data => {
      this.type = data['type'];
    });

    this.lang = this.languageService.getCurrentLang();
    this.srAccountId = this.sharedService.getCsnAccountId('id');
    this.userCSN = this.sharedService.getCsnAccountId('csn');
    this.getCountryLOV();

    this.form = this.fb.group({
      licenseIssuingAuthority: [null, Validators.required],
      licenceType: [null],
      licenceNumber: [null, [Validators.maxLength(150), customValidator('safeText')]],
      tradeNameEn: [null, [Validators.required, customValidator('english'), Validators.maxLength(150)]],
      tradeNameAr: [null, [Validators.required, customValidator('arabic'), Validators.maxLength(150)]],
      issueDate: this.fb.group({
        day: [null],
        month: [null],
        year: [null],
      }),
      expiryDate: this.fb.group({
        day: [null],
        month: [null],
        year: [null],
      }),
      membershipExpiryDate: this.fb.group({
        day: [{ value: null, disabled: true }],
        month: [{ value: null, disabled: true }],
        year: [{ value: null, disabled: true }],
      }),
      nationality: [null, Validators.required],
      legalStatus: [null, Validators.required],
      firmType: [null, Validators.required],
      officeAddress: [null, [Validators.required, Validators.maxLength(30), customValidator('safeText')]],
      buildingName: [null, [Validators.maxLength(30), customValidator('safeText')]],
      street: [null, [Validators.required, Validators.maxLength(100), customValidator('safeText')]],
      address: ['', [Validators.required, Validators.maxLength(200), customValidator('safeText')]],
      countryCode: ['971'],
      phoneNumber: [null, [customValidator('mobile')]],
      contactEmail: [null, [Validators.required, customValidator('email'), Validators.maxLength(50)]],
      hasParentCompany: [],
      parentCompanyLicenceNumber: ["", [Validators.maxLength(100), customValidator('safeText')]],
      parentCompanyMemberNumber: ["", [Validators.maxLength(100), customValidator('safeText')]],
      owners: [null, Validators.required],
      activitySearch: [null, Validators.required],
      activitiesConfirmed: [null, Validators.required],
      membershipYear: [null],
      documentTradeLicense: [null],
      documentMoA: [null],
      legalName: [null, [Validators.maxLength(250), customValidator('safeText')]],
      legalEmail: [null, [customValidator('email'), Validators.maxLength(50)]],
      legalContactCountryCode: ['971'],
      legalContactNumber: [null, [customValidator('mobile')]],
      legalMemberNumber: ['', [Validators.maxLength(50), customValidator('safeText')]],
      decreeNumber: ['', [Validators.maxLength(100), customValidator('safeText')]],
      decreeIssueDate: this.fb.group({
        day: [null],
        month: [null],
        year: [null],
      }),
      paymentMethod: [null, Validators.required],
      primaryAddressId: [null]
    });

    this.srSubType = ServiceName.MEMBERSHIP_NEW;
    if (this.type == 'amendment') {
      this.srSubType = ServiceName.MEMBERSHIP_AMENDMENT;
    }
    if (this.type == 'renew') {
      this.srSubType = ServiceName.MEMBERSHIP_RENEW;
    }

    this.setInitialValues();
    // Call the function to disbale fields
    Utils.disableFieldsIfAmendmentOrRenew(this.type, this.form);

    this.membershipYears = this.optionTranslateService.getOptions(
      'membershipYearsList',
      this.lang
    );

    // SR Number in the url
    this.sharedService.getSrNumber(this.route).subscribe(srNumber => {
      if (srNumber) {
        this.isSrFromQueryParams = true;
        this.srNumber = srNumber;
        this.getSRDetail();
      }
    });

    // in case of draft
    if (sessionStorage.getItem("srNumber") && this.srNumber == "") {
      let storeSrNumber: any = sessionStorage.getItem("srNumber");
      this.srNumber = storeSrNumber;
      this.getSRDetail();
    }

    if (this.form.get('licenseIssuingAuthority')) {
      this.form.get('licenseIssuingAuthority')?.valueChanges.subscribe(() => {
        if (this.form.get('licenseIssuingAuthority')?.disabled) {
          return
        }
        this.form.get('licenceType')?.setValue(null); // reset child
      });
    }
    if (this.srSubType == ServiceName.MEMBERSHIP_AMENDMENT || this.srSubType == ServiceName.MEMBERSHIP_RENEW) {
      this.getMemberDocuments();
    }
  }

  memberships: any = [];
  membershipId: string = "";
  // This method is used to set initial values
  setInitialValues() {
    this.getActiveMembership();
    //only for Member
    if ((this.srSubType == ServiceName.MEMBERSHIP_AMENDMENT || this.srSubType == ServiceName.MEMBERSHIP_RENEW) && this.userType != UserType.MEMBER) {
      this.sharedService.toastMessage('INFO', 'ONLY_MEMBER_ALLOWED', 'info', true);
      this.router.navigate([RoutesPath.DASHBOARD]);
      return
    }
    if (this.memberships.length < 1 && this.srSubType != ServiceName.MEMBERSHIP_NEW) {
      this.sharedService.toastMessage('INFO', 'NO_ACTIVE_MEMBERSHIP', 'info', true);
      this.router.navigate([RoutesPath.DASHBOARD]);
      return
    }
    if ((this.srSubType == ServiceName.MEMBERSHIP_AMENDMENT || this.srSubType == ServiceName.MEMBERSHIP_RENEW) && !this.userCSN) {
      this.sharedService.toastMessage('INFO', 'SELECTED_MEMBERSHIP_INPROGRESS', 'info', true);
      this.router.navigate([RoutesPath.DASHBOARD]);
      return
    }

    // New membership then set account id and csn to blank
    if (this.srSubType == ServiceName.MEMBERSHIP_NEW) {
      this.srAccountId = "";
      this.userCSN = "";
    }
  }
  // This method is used to get active membership
  getActiveMembership() {
    this.membershipId = this.userCSN;
    let userDetails: any = this.sharedService.getUserProfile();
    let accounts = Utils.checkIsArray(userDetails?.Account) ?? [];
    const filteredAccounts = accounts.filter((acc: any) => {
      // must have CSN
      if (!acc.CSN || acc.CSN.trim() === '') {
        return false;
      }
      // must not be inactive
      if (this.sharedService.isInactiveMemberAccount(acc)) {
        return false;
      }
      // must have expiry date
      if (!acc["DC Expiry Date"]) {
        return false;
      }
      // use common method for membership expiry validation
      let isMembershipValid = Utils.isMembershipValid(acc["DC Expiry Date"]);
      if (this.srSubType == ServiceName.MEMBERSHIP_AMENDMENT && !isMembershipValid) {
        return false;
      }
      // use common method for membership expiry validation
      return true;
    });
    this.memberships = filteredAccounts;
  }

  // This method is used to handle the on init lifecycle hook
  ngOnInit(): void {
    //has parent company
    this.form.get('hasParentCompany')?.valueChanges.subscribe((value: any) => {
      this.toggleRequired(value == 'Yes' ? true : false, ['parentCompanyLicenceNumber',
        'parentCompanyMemberNumber']);
      if (!value) {
        this.resetParentCompanyValidationState(true);
      }
    });

    ['parentCompanyLicenceNumber', 'parentCompanyMemberNumber'].forEach((controlName: string) => {
      this.form.get(controlName)?.valueChanges.subscribe(() => {
        if (this.form.get('hasParentCompany')?.value === 'Yes') {
          this.resetParentCompanyValidationState(true);
        }
      });
    });
  }

  // This method is used to handle nationality change event
  onNationalityChange() {
    const value = this.form.get('nationality')?.value;
    const phoneControl = this.form.get('phoneNumber');
    const countryCodeControl = this.form.get('countryCode');
    if (!phoneControl || !countryCodeControl) {
      return;
    }
    if (value == FieldValue.UAE || value == FieldValue.UAE_AR) {
      phoneControl.setValidators([Validators.required, customValidator('mobile', 'UAE')]);
      countryCodeControl.setValue("971");
      countryCodeControl.disable();
    } else {
      phoneControl.setValidators([Validators.required, customValidator('mobile')]);
      countryCodeControl.enable();
    }
    phoneControl.updateValueAndValidity({ emitEvent: false });
    countryCodeControl.updateValueAndValidity({ emitEvent: false });
  }

  pendingSrNumber: string = "";
  pendingSrDetail: any = "";
  // This method is used to check for pending sr
  checkForPendingSr() {
    if (this.srNumber == "" && (this.srSubType == ServiceName.MEMBERSHIP_RENEW
      || this.srSubType == ServiceName.MEMBERSHIP_AMENDMENT
      || this.srSubType == ServiceName.MEMBERSHIP_CANCELLATION) && this.userCSN
    ) {
      this.srDetailLoading = true;
      this.sharedService.checkInprogressApplication(this.userCSN, ServiceName.MEMBERSHIP, this.srSubType)
        .subscribe({

          next: (response: any) => {
            if (response["Error Code"] == null && response["Error Message"] == null && response?.SiebelMessage['DC Community Portal SR']) {
              const srData = response?.SiebelMessage['DC Community Portal SR'];
              const srRecords = Array.isArray(srData) ? srData : [srData];
              const pendingSrNumbers = srRecords
                .map((sr: any) => sr?.['SR Number'])
                .filter(Boolean);
              if (pendingSrNumbers.length) {
                this.pendingSrNumber = pendingSrNumbers.join(', ');
                this.pendingSrDetail = Array.isArray(srData) ? srRecords : srData;
                // this.sharedService.toastMessage('INFO', 'MANGE_SIGNATORY_SR_PENDING', 'info');                
                return;
              }
            } else {
              this.srDetailLoading = false;
              if (this.srNumber == "" || !this.srNumber) {
                this.getMemberDetail();
              }
            }
          }
        })
    } else {
      this.srDetailLoading = false;
      if (this.userCSN && this.srNumber) {
        if (this.srSubType == ServiceName.MEMBERSHIP_RENEW) {
          this.getMemberDetail(true);
        }
      }
    }
  }

  // This method is used to toggle required using condition and controls
  private toggleRequired(condition: boolean, controls: string[]): void {
    Utils.addRemoveValidation(this.form, condition, controls);
  }

  private updateOwnerActivityRequirements(isOffshore: boolean): void {
    this.isOffshoreLicenseType = isOffshore;
    const controls = [
      { control: this.form.get('owners'), validators: [Validators.required] },
      { control: this.form.get('activitySearch'), validators: [Validators.required] },
      { control: this.form.get('activitiesConfirmed'), validators: [Validators.required] }
    ];

    controls.forEach(({ control, validators }) => {
      if (!control) {
        return;
      }
      if (isOffshore) {
        control.clearValidators();
      } else {
        control.setValidators(validators);
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  //get folder id and attachments
  srFolder!: FolderResponse;
  mainFromSectionDocs: AllowedDocumentType[] = [];
  memberDocuments: any[] = [];
  memberDocumentFolderId: string | null = null;
  memberDocumentsFetched: boolean = false;
  shouldClearMoaFromMemberDocs: boolean = false;

  // This method is used to open folder
  loadingDoc: boolean = false;
  openFolder() {
     this.loadingDoc = true;
    let documentType = DocumentServiceType.MEMBERSHIP;
    let isLabels = !this.srNumber; // true if srNumber is empty
    if (this.form.get('licenceType')?.value == FieldValue.OFFSHORE_LICENSE ||
      this.form.get('licenceType')?.value == FieldValue.OFFSHORE_LICENSE_AR) {
      documentType = DocumentServiceType.MEMBERSHIP_OFLIC;
    }

    if (this.form.get('licenseIssuingAuthority')?.value == FieldValue.WITHOUT_LICENSE ||
      this.form.get('licenseIssuingAuthority')?.value == FieldValue.WITHOUT_LICENSE_AR) {
      documentType = DocumentServiceType.MEMBERSHIP_WITHOUT_LIC;
    }
    this.sharedService.getDocuments(this.srNumber, documentType, isLabels)
      .subscribe({

        next: (response: any) => {
          if (response.success == true) {
            // If SR exists → response.data, otherwise → response
            this.srFolder = response.data ? response.data : response;
            if (this.srFolder?.allowedDocumentTypes && this.srFolder?.allowedDocumentTypes?.length > 0) {
              this.loadingDoc = false;
              this.mainFromSectionDocs = this.srFolder.allowedDocumentTypes?.filter((d: any) => !d.forUISection);
              this.mergeMemberDocumentsIntoSrFolder();
            }
          } else {
            this.sharedService.toastMessage("error", response.message, 'error');
          }
          this.getMemberDocuments();
        }
      });
  }
  // This method is used to check required documents
  checkRequiredDocuments(): boolean {
    // If there are no docs to check, nothing is missing
    if (!this.mainFromSectionDocs || this.mainFromSectionDocs.length === 0) {
      return false;
    }
    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    // ViewChildren not ready or not fully rendered yet – defer document checks
    if (fileCmps.length < this.mainFromSectionDocs.length) {
      return false;
    }

    let hasMissing = false;
    for (let index = 0; index < this.mainFromSectionDocs.length; index++) {
      const doc = this.mainFromSectionDocs[index];
      const cmp = fileCmps[index];
      // Safety guard; should not happen due to length check above
      if (!cmp) continue;

      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing((doc)?.mandatory)) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }

  // This method is used to hide/show and make fields required based on authority and license type
  onLicenseAuthorityAndTypeChagne(skipLicenseDetail: boolean = false) {

    const offshoreFields = ['legalName', 'legalEmail', 'legalContactCountryCode', 'legalContactNumber', 'legalMemberNumber'];
    const withoutLicenseFields = ['decreeNumber', 'decreeIssueDate.day', 'decreeIssueDate.month', 'decreeIssueDate.year'];
    const otherLicenseDateFields = ['issueDate.day', 'issueDate.month', 'issueDate.year', 'expiryDate.day', 'expiryDate.month', 'expiryDate.year'];
    const licenceNumberField = ['licenceNumber']; //'licenceType', 'legalStatus', 
    const licenceTypeField = ['licenceType']; //'licenceType', 'legalStatus', 

    const licenceType = this.form.get('licenceType')?.value;
    const issuingAuthority = this.form.get('licenseIssuingAuthority')?.value;
    const isOffshore = licenceType == FieldValue.OFFSHORE_LICENSE || licenceType == FieldValue.OFFSHORE_LICENSE_AR;
    const isWithoutLicense = issuingAuthority == FieldValue.WITHOUT_LICENSE || issuingAuthority == FieldValue.WITHOUT_LICENSE_AR;

    this.updateOwnerActivityRequirements(isOffshore);

    const firmTypeControl = this.form.get('firmType');
    if (firmTypeControl) {
      const mainOfficeValue = this.lang === 'ar' ? FieldValue.FIRMT_TYPE_MAINOFFICE_AR : FieldValue.FIRMT_TYPE_MAINOFFICE;
      if (isOffshore && firmTypeControl.value !== mainOfficeValue) {
        firmTypeControl.setValue(mainOfficeValue);
      }
      if (isOffshore && !firmTypeControl.disabled) {
        firmTypeControl.disable({ emitEvent: false });
      } else if (!isOffshore && firmTypeControl.disabled && this.showActions) {
        firmTypeControl.enable({ emitEvent: false });
      }
    }

    this.toggleRequired(isOffshore, offshoreFields);

    if (isWithoutLicense) {
      this.isTradeLicenseValid = true;
    }
    this.toggleRequired(isWithoutLicense, withoutLicenseFields);

    if (isWithoutLicense && !licenceType) {
      this.openFolder();
      this.toggleRequired(false, [...licenceNumberField, ...otherLicenseDateFields, ...licenceTypeField]);
    } else {
      if (isWithoutLicense && licenceType) {
        this.toggleRequired(false, [...licenceNumberField, ...otherLicenseDateFields]);
      } else {
        this.toggleRequired(true, [...licenceNumberField, ...otherLicenseDateFields, ...licenceTypeField]);
      }
      if (licenceType) {
        this.openFolder();
      }
    }
    // this.toggleRequired(false, ['licenceNumber'])
    // if (!isWithoutLicense && !isOffshore) {
    //   const licenseNumber = ['licenceNumber']
    //   this.toggleRequired(true, [...licenseNumber, ...licenceNumberField, ...otherLicenseDateFields]);
    // }

    if (!skipLicenseDetail) {
      this.validateTradeLicense();
    }
  }
  // This method is used to get DET license detail
  getLicenseDetail() {
    if (this.form.disabled) {
      this.isTradeLicenseValid = true;
      return;
    }
    if (this.srSubType != ServiceName.MEMBERSHIP_NEW) {
      return;
    }
    this.validateTradeLicense();
  }

  getDETLicenseDetail() {
    if (this.form.get('licenseIssuingAuthority')?.value != FieldValue.DET) {
      return;
    }

    //Clear form controls values only if new sr
    if (this.srNumber == "") {
      this.ownerList = [];
      this.selectedActivities = [];
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);

        if (!['licenceType', 'licenseIssuingAuthority', 'licenceNumber'].includes(key)) {
          if (control instanceof FormControl) {
            control.setValue(null);
          } else if (control instanceof FormGroup) {
            control.reset();
          }
        }
      });
    }

    const requestPayload: LicenseRequestPayload = {
      LicenseNumber: this.form.get('licenceNumber')?.value
    };

    this.api.post<LicenseRequestPayload, DcciLicenseResponse>(
      ApiRoot.DCCIProfessionalLicenseDetails,
      ApiEndPoints.GET_MEMBER_DATA,
      requestPayload
    ).subscribe({
      next: (response: DcciLicenseResponse) => {
        if (response?.DCCILicense && response['ErrorCode'] == null && response['ErrorMessage'] == null) {
          const license = response.DCCILicense;

          const issueDate = license["IssueDate"]
            ? new Date(license["IssueDate"])
            : null;

          const licenseExpiryDate = license["ExpiryDate"]
            ? new Date(license["ExpiryDate"])
            : null;

          const today = new Date();
          let expiryDate = licenseExpiryDate && licenseExpiryDate > today ? licenseExpiryDate : null;
          const licenseExpiryDateObj = {
            day: expiryDate ? expiryDate.getDate().toString().padStart(2, '0') : null,
            month: expiryDate ? (expiryDate.getMonth() + 1).toString().padStart(2, '0') : null,
            year: expiryDate ? expiryDate.getFullYear() : null,
          }
          const matchedCountry = this.getMatchedCountryValue(license.LicenseNationalityDescENG);

          this.form.patchValue({
            tradeNameEn: license.TradeNameENG,
            tradeNameAr: license.TradeNameAR,
            issueDate: {
              day: issueDate ? issueDate.getDate().toString().padStart(2, '0') : null,
              month: issueDate ? (issueDate.getMonth() + 1).toString().padStart(2, '0') : null,
              year: issueDate ? issueDate.getFullYear() : null,
            },
            expiryDate: licenseExpiryDateObj,
            membershipExpiryDate: licenseExpiryDateObj,
            nationality: matchedCountry, //license.LicenseNationalityDescENG
            legalStatus: license.LicenseLegalTypeDescENG,
            firmType: license.FirmType,
            officeAddress: license.FloorNumber,
            address: license.Address,
            buildingName: license.BuildingNumber,
            countryCode: license.PhNumber ? license.PhNumber.replace('+', '').split('-')[0] : "971",
            phoneNumber: license.PhNumber ? license.PhNumber.split('-')[1] : "",
            contactEmail: license.Email,
            street: "",
            membershipYear: ""
          });

          if (license.LicenseNationalityDescENG) {
            if (license.LicenseNationalityDescENG == FieldValue.UAE || license.LicenseNationalityDescENG == FieldValue.UAE_AR) {
              this.form.get('phoneNumber')?.setValidators([customValidator('mobile', 'UAE')]);
              this.form.get('countryCode')?.setValue("971");
              this.form.get('countryCode')?.disable();
            }
          }
          if (license.TradeNameENG) {
            this.step = 2;
          }
          //License Activities
          if (license?.ListOfDCCIActivity) {
            let licenseActivities: any = [];
            license.ListOfDCCIActivity?.DCCIActivity?.map((activity) => {
              if (activity.ActivityCode && activity.ActivityDescENG &&
                activity.ActivityDescAR
              ) {
                let activityObj = {
                  codeFee: activity.ActivityCode,
                  description: activity.ActivityDescAR,
                  code: activity.ActivityCode,
                  name: activity.ActivityDescENG,
                  status: activity.ActivityStatusDescENG,
                  accountActivityId: "",
                  endDate: ""
                }
                licenseActivities.push(activityObj)
              }
            })
            this.addAactivity(licenseActivities);
          }
          if (license?.ListOfDCCIPartner?.DCCIPartner) {
            // let partners: any = [];
            license.ListOfDCCIPartner?.DCCIPartner?.map((p) => {
              // 1:Shares Owner,
              // 2:Manager
              // 3:Authorised signatory
              if (p?.PartnerCancelDate == null && p.PartnerNameENG && p.PartnerNameAR) {

                let roles: string[] = [];
                // Determine primary role based on Legal Type
                switch (p.PartnerLegalTypeDescENG) {
                  case 'Partners':
                  case 'Partner':
                  case 'Partner /Owner':
                  case 'Shares Owner':
                    roles.push('Shares Owner');
                    break;

                  case 'Manager':
                  case 'Managing Partner':
                    roles.push('Manager');
                    break;

                  default:
                    roles.push('Authorised Signatory');
                    break;
                }

                // Add additional combined role when PercentOwnership exists
                if (p.PercentOwnership) {
                  roles.push('Shares Owner');
                  roles.push('Authorised Signatory');
                }

                let object = {
                  ownerId: p.PartnerId,
                  roles: roles,//[role],
                  typeOfAddUser: 'New profile', //new profile
                  partnerType: 'Individual', //individual
                  isResident: p.PassportNumber ? 'No' : 'Yes',
                  emiratesId: '',
                  emiratesDob: '',
                  passportNumber: p.PassportNumber,
                  passportExpiry: '',
                  nameEn: p.PartnerNameENG,
                  nameAr: p.PartnerNameAR,
                  nationality: this.getMatchedCountryValue(p.PartnerNationalityDescENG),
                  companyNameEn: '',
                  companyNameAr: '',
                  share: p.PercentOwnership,
                  mobile: '',
                  countryCode: '',
                  email: '',
                  endDate: ''
                }
                const existingIndex = this.ownerList.findIndex((o: any) => o.ownerId === p.PartnerId);
                if (this.ownerList.length > 0 && existingIndex !== -1) {
                  this.ownerList[existingIndex].roles = roles; //.push(role);
                } else {
                  object["ownerId"] = p.PartnerId;
                  this.ownerList.push(object);
                }
              }
            })
            if (this.ownerList) {
              this.updateOwnersControlState();

              this.showShareMsg = this.ownerTotalShare != null &&
                (this.ownerTotalShare < 100 || this.ownerTotalShare > 100);

              this.step == 3;
            }
          }
        }
      },
      error: (err) => {
        console.error('Error fetching license data:', err);
      }
    });
  }

  // get sr detail
  // This method is used to get sr detail
  getSRDetail() {
    this.srDetailLoading = true;
    const requestPayload = {
      "body": {
        "ProcessName": "DC Get SR Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": `${this.srSubType} Page`,
        "SR Number": this.srNumber,
        "IOName": SrIOName.MEMBERSHIP
      }
    };
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_SR_DETAIL,
      requestPayload
    ).subscribe({
      next: (response: any) => {
        if (response?.SiebelMessage && response?.SiebelMessage["DC Mem SR"]) {
          this.srDetailLoading = false;
          const serviceRequest = response?.SiebelMessage["DC Mem SR"];
          this.currentSrStatus = String(serviceRequest["Status"] ?? SrStatus.Draft).trim();
                    
          this.srAccountId = serviceRequest['Account Id'];
          const issueDate = serviceRequest["DC License Issue Date"] !=''
            ? new Date(serviceRequest["DC License Issue Date"])
            : "";
          const expiryDate = serviceRequest["DC License Expiry Date"] != ''
            ? new Date(serviceRequest["DC License Expiry Date"])
            : "";

          const memExpiryDate = serviceRequest["DC Membership Expiry Date"] != ''
            ? new Date(serviceRequest["DC Membership Expiry Date"])
            : (serviceRequest["DC License Expiry Date"] != '' ? new Date(serviceRequest["DC License Expiry Date"]) : "");

          const decreeIssueDate = serviceRequest["DC Decree Issue Date"]
            ? new Date(serviceRequest["DC Decree Issue Date"])
            : null;

          // this.selectedMemberExpiryDate = memExpiryDate;
          // this.selectedMemberLicenseExpiryDate = expiryDate;
          let cutAddress = serviceRequest["DC Mem SR CUT Address"] ? serviceRequest["DC Mem SR CUT Address"] : "";
          this.form.patchValue({
            licenseIssuingAuthority: serviceRequest["DC License Issuing Authority"],
            licenceType: serviceRequest["DC License Type"],
            licenceNumber: serviceRequest["DC License Registration Number"] ? serviceRequest["DC License Registration Number"] : '',

            tradeNameEn: serviceRequest["DC Member Name(English)"],
            tradeNameAr: serviceRequest["DC Member Name(Arabic)"],
            issueDate: {
              day: issueDate ? issueDate.getDate().toString().padStart(2, '0') : null,
              month: issueDate ? Number(issueDate.getMonth() + 1).toString().padStart(2, '0') : null,
              year: issueDate ? issueDate.getFullYear() : null,
            },
            expiryDate: {
              day: expiryDate ? expiryDate.getDate().toString().padStart(2, '0') : null,
              month: expiryDate ? Number(expiryDate.getMonth() + 1).toString().padStart(2, '0') : null,
              year: expiryDate ? expiryDate.getFullYear() : null,
            },
            membershipExpiryDate: {
              day: memExpiryDate ? memExpiryDate.getDate().toString().padStart(2, '0') : null,
              month: memExpiryDate ? (memExpiryDate.getMonth() + 1).toString().padStart(2, '0') : null,
              year: memExpiryDate ? memExpiryDate.getFullYear() : null,
            },
            nationality: serviceRequest['DC Respondent Country'] ? serviceRequest['DC Respondent Country'] : null, //cutAddress ? cutAddress["Country"] : "",
            legalStatus: serviceRequest["DC Legal Status"],
            firmType: serviceRequest["DC Firm Type"],

            officeAddress: cutAddress ? cutAddress["Descriptor"] : "",
            buildingName: cutAddress ? cutAddress["Apartment Number"] : "",
            address: cutAddress ? cutAddress["DC Address English Name"] : "",
            street: cutAddress ? cutAddress["Street Address"] : "",
            countryCode: serviceRequest["DC Holder Phone"] ? Utils.parseMobile(serviceRequest["DC Holder Phone"], 'cc') : '971', //license["Main Phone Number"].split('-')[0],
            phoneNumber: serviceRequest["DC Holder Phone"] ? Utils.parseMobile(serviceRequest["DC Holder Phone"], 'number') : '',
            contactEmail: serviceRequest["DC CRB Primary Email"],
            hasParentCompany: serviceRequest["Parent Company"],
            parentCompanyLicenceNumber: serviceRequest["Parent Comp Lic Number"],
            parentCompanyMemberNumber: serviceRequest["Parent Comp Mem Num"],
            membershipYear: serviceRequest["Membership Years"],

            decreeNumber: serviceRequest["DC Decree Number"],
            legalContactCountryCode: serviceRequest["DC Legal Contact"] ? Utils.parseMobile(serviceRequest["DC Legal Contact"], 'cc') : '971', //license["Main Phone Number"].split('-')[0],
            legalContactNumber: serviceRequest["DC Legal Contact"] ? Utils.parseMobile(serviceRequest["DC Legal Contact"], 'number') : '',

            legalEmail: serviceRequest["DC Legal Email Addr"],
            legalMemberNumber: serviceRequest["DC Legal Member Number"],
            legalName: serviceRequest["DC Legal Name"],
            decreeIssueDate: {
              day: decreeIssueDate ? decreeIssueDate.getDate().toString().padStart(2, '0') : null,
              month: decreeIssueDate ? (decreeIssueDate.getMonth() + 1).toString().padStart(2, '0') : null,
              year: decreeIssueDate ? decreeIssueDate.getFullYear() : null,
            },
            paymentMethod: serviceRequest["DC Payment Method"] ? serviceRequest["DC Payment Method"] : null,
            primaryAddressId: cutAddress && cutAddress["Primary Address Id"] ? cutAddress["Primary Address Id"] : ''
          });
          this.initialTradeLicenseValues = {
            issuingAuthority: this.normalizeTradeLicenseField(this.form.get('licenseIssuingAuthority')?.value),
            licenceType: this.normalizeTradeLicenseField(this.form.get('licenceType')?.value),
            licenceNumber: this.normalizeTradeLicenseField(this.form.get('licenceNumber')?.value),
          };
          this.onLicenseAuthorityAndTypeChagne(true);
          this.lockTradeLicenseFieldsForNewMembership();
          if (serviceRequest["Parent Account Id"] || (serviceRequest["Parent Company"] && serviceRequest["Parent Comp Lic Number"])) {
            this.licenseMember = {
              'Error Code': "",
              MemberName: serviceRequest["Parent Company Name English"],
              MemberNameAra: serviceRequest["Parent Company Name Arabic"],
              ExpiryDate: serviceRequest["Parent Company Expiry Date"]
            }
            this.isValidMemberNumber = true;
          }

          if (serviceRequest["DC License Issuing Authority"] && (serviceRequest["DC License Type"]
            || serviceRequest["DC License Issue Date"] || serviceRequest["DC Decree Issue Date"])) {
            this.isTradeLicenseValid = true;
            this.step = 2;
            // LC offshore then show last step
            if (this.isOffshoreLicenseType) {
              this.step = 5;
            }
          }

          // Ownership details
          if (serviceRequest["DC Mem SR Relationship"]) {
            let membershipKey = serviceRequest["DC Mem SR Relationship"];

            const relationships = Utils.checkIsArray(membershipKey);
            relationships?.map((p: any, index: number) => {

              let relationshipKey = p["Relationship"] ? p["Relationship"] : "Owner";
              let jobTitle = p["DC Job Title"] ? p["DC Job Title"] : "Owner";
              let isAuthSignatory = p["DC Authorized Signatory"] ? p["DC Authorized Signatory"] : "N";

              const role = this.getRolesFromRelationshipJobTitle(relationshipKey, jobTitle, isAuthSignatory);
              let passportExpDate = p['DC PP Exp Date'] ? p['DC PP Exp Date'].split('/') : "";
              let memberDob = p["Member DOB"] ? p["Member DOB"].split('/') : "";

              let object = {
                ownerId: index + 1,
                roles: role,
                typeOfAddUser: 'New profile',
                partnerType: p['DC Relationship Type'] == 'Organization' ? 'Company' : 'Individual',
                isResident: p['UAE Citizen'],
                emiratesId: p["DC Emirates ID Number"],
                emiratesDob: {
                  day: memberDob ? memberDob[1] : null,
                  month: memberDob ? memberDob[0] : null,
                  year: memberDob ? Number(memberDob[2].toString()) : null,
                },
                passportNumber: p["DC DED Partner Passport Number"],
                passportExpiry:
                {
                  day: passportExpDate ? passportExpDate[1] : null,
                  month: passportExpDate ? passportExpDate[0] : null,
                  year: passportExpDate ? Number(passportExpDate[2].toString()) : null,
                },
                nameEn: p["DC Contact Name(ENGLISH)"],
                nameAr: p["DC Contact Name(ARABIC)"],
                nationality: p["DC Contact Nationality"],
                companyNameEn: p["DC Name(ENGLISH)"],
                companyNameAr: p["DC Name(ARABIC)"],
                share: p["DC Ownership Percent"],
                mobile: p["DC Cell Phone"] ? Utils.parseMobile(p["DC Cell Phone"], 'number') : '',
                countryCode: p["DC Cell Phone"] ? Utils.parseMobile(p["DC Cell Phone"], 'cc') : '',
                email: p["DC Email Address"],
                accountMembershipId: p["Account Membership Id"] ? p["Account Membership Id"] : '',
                endDate: p["DC End Date"] ? p["DC End Date"] : ''
              }
              const existingIndex = this.ownerList.findIndex((o: any) => o.ownerId === p.ownerId);
              if (this.ownerList.length > 0 && existingIndex !== -1) {
                this.ownerList[existingIndex].roles.push(role);
              } else {
                object["ownerId"] = index + 1;
                this.ownerList.push(object);
              }
            });

            if (this.ownerList) {
              this.updateOwnersControlState();
              this.showShareMsg = this.ownerTotalShare != null &&
                (this.ownerTotalShare < 100 || this.ownerTotalShare > 100);
              this.step = 3;
            }
          }

          //Activities
          if (serviceRequest["DC Mem SR Activity Code"]) {
            let licenseActivities: any = [];
            let activities = Utils.checkIsArray(serviceRequest["DC Mem SR Activity Code"]);
            activities?.map((activity: any) => {
              let activityObj = {
                id: `${Utils.generateUniqueId()}_${activity["DC Activity Code"]}`,
                codeFee: activity["DC Activity Fee"],
                description: activity["DC Activity Description Arabic"],
                code: activity["DC Activity Code"],
                name: activity["DC Activity Description"], //activity["Name"],
                descriptionOtherEn: activity['DC Special Act Code Eng'],
                descriptionOtherAr: activity['DC Special Act Code Ara'],
                accountActivityId: activity["Account Activity Id"] ? activity["Account Activity Id"] : '', //activity["Name"] ? activity["Name"] : '',
                endDate: activity["DC End Date"] ? activity["DC End Date"] : ''
              }
              licenseActivities.push(activityObj);
            })
            this.addAactivity(licenseActivities);
            if (licenseActivities) {
              this.step = 5;
              this.form.get('activitiesConfirmed')?.setValue('Yes');
            }
          }
          // if (this.srSubType != ServiceName.MEMBERSHIP_NEW && cutAddress && cutAddress["DC Address English Name"] != "") {
          //   this.form.get('address')?.disable();
          // }

          if (this.sharedService.enableNotesOption(serviceRequest["Status"])) {
            this.showNotes = true;
            this.srInprgoressAwating = true;
          }

          if (serviceRequest["Status"] != SrStatus.Draft && serviceRequest["Status"] != SrStatus.AwaitingClarification && serviceRequest["Status"] != SrStatus.Open) {
            this.showActions = false;
            this.form.disable();
          }
          // enable issue date and expiry date if they are null, so user can enter the values
          if(serviceRequest["Status"] == SrStatus.Draft || serviceRequest["Status"] == SrStatus.AwaitingClarification){
            if (!issueDate) {
              this.form.get('issueDate')?.enable();
            }
            if (!expiryDate) {
              this.form.get('expiryDate')?.enable();
            }
          }

        } else {
          this.srDetailLoading = false;
          if (response["Error Message"]) {
            this.errorMsg = response["Error Message"];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      },

      error: (err) => {
      }
    });
  }

  get showSaveAndCloseAction(): boolean {
    return this.sharedService.canShowSaveAndClose(this.currentSrStatus);
  }

  //get roles based on relationship and job title
  // This method is used to get roles from relationship job title using relationship key, job title, and is auth signatory
  getRolesFromRelationshipJobTitle(
    relationshipKey: string,
    jobTitle: string,
    isAuthSignatory: 'Y' | 'N'
  ): string[] {
    let roles: string[] = [];

    // Case 1: Contact Of + Authorised Signatory
    if (relationshipKey === 'Contact Of' && jobTitle === 'Authorised Signatory') {
      roles = ['Authorised Signatory'];
    }

    // Case 2: Owner or Partner + Manager
    else if ((relationshipKey === 'Owner' || relationshipKey === 'Partner' || relationshipKey === 'Shares Owner' || relationshipKey === 'Est. Owner') && jobTitle === 'Manager') {
      roles = ['Shares Owner', 'Manager'];
    }

    // Case 3: Owner or Partner + Owner
    else if ((relationshipKey === 'Owner' || relationshipKey === 'Partner' || relationshipKey === 'Est. Owner') && jobTitle === 'Owner') {
      roles = ['Shares Owner'];
    }

    // Case 4: Contact Of + Manager
    else if (relationshipKey === 'Contact Of' && jobTitle === 'Manager') {
      roles = ['Manager'];
    }

    // Fallback for Owner/Partner
    if (roles.length === 0 && (relationshipKey === 'Owner' || relationshipKey === 'Partner' || relationshipKey === 'Shares Owner')) {
      roles = ['Shares Owner'];
    }

    // Add 'Authorised Signatory' if marked and not already included
    if (isAuthSignatory === 'Y' && !roles.includes('Authorised Signatory')) {
      roles.push('Authorised Signatory');
    }

    if (roles.length === 0) {
      roles = ['Manager'];
    }
    return roles;
  }

  //Validate member license
  isValidMemberNumber: boolean = false;

  private normalizeComparisonValue(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private resetParentCompanyValidationState(clearMemberDetails: boolean = false): void {
    this.isValidMemberNumber = false;
    this.memberLicenseError = false;
    this.loaderValidateMemLicense = false;
    if (clearMemberDetails) {
      this.licenseMember = "";
    }
  }

  private isSameAsCurrentParentCompanyDetails(): boolean {
    const parentLicenseNo = this.normalizeComparisonValue(this.form.get('parentCompanyLicenceNumber')?.value);
    const parentMemberNo = this.normalizeComparisonValue(this.form.get('parentCompanyMemberNumber')?.value);
    const currentLicenseNo = this.normalizeComparisonValue(this.form.get('licenceNumber')?.value);
    const currentMemberNo = this.normalizeComparisonValue(this.userCSN);

    return (!!parentLicenseNo && parentLicenseNo === currentLicenseNo) ||
      (!!parentMemberNo && parentMemberNo === currentMemberNo);
  }

  // This method is used to check member license detail
  checkMemberLicenseDetail() {
    let licenseNo = this.form.get('parentCompanyLicenceNumber')?.value;
    let memberNo = this.form.get('parentCompanyMemberNumber')?.value;

    if (!memberNo || !licenseNo) {
      this.form.get('parentCompanyLicenceNumber')?.markAsTouched();
      this.form.get('parentCompanyMemberNumber')?.markAsTouched();
      return
    }

    if (this.isSameAsCurrentParentCompanyDetails()) {
      this.form.get('parentCompanyLicenceNumber')?.setValue('');
      this.form.get('parentCompanyMemberNumber')?.setValue('');
      this.resetParentCompanyValidationState();
      this.memberLicenseError = true;
      // this.licenseMember = {
      //   'Error Code': 1,
      //   'Error Message': errorMessage
      // };
      this.sharedService.toastMessage('ERROR', 'PARENT_COMPANY_MEMBER_MESSAGE', 'error', true);
      return;
    }

    this.resetParentCompanyValidationState(true);
    this.loaderValidateMemLicense = true;

    const requestPayload = {
      "body": {
        "ProcessName": "DC Validate Member Info WF",
        "LicenseNo": licenseNo,
        "LicenseAuth": '',
        "LicenseType": '',
        "MemberNo": memberNo,
      }
    }
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.VALIDATE_MEMBER_LICENSE,
      requestPayload
    ).subscribe({

      next: (response: any) => {
        this.loaderValidateMemLicense = false;
        if (response['Error Code'] == 0) {
          this.licenseMember = response;
          this.isValidMemberNumber = true;
        } else {
          this.licenseMember = response;
          this.memberLicenseError = true;
        }
      },
      // This method is used to handle the error callback using e
      error: (e) => {
        this.loaderValidateMemLicense = false;
      }
    })
  }

  //Lov change
  // This method is used to handle lov chnage event using option
  onLovChnage(option: any) {
    if (option) {
      //Set selected lov
      this.lovService.setLovSelection(option.lovType, option.selectedLov);
      if (option.lovType == LookupKey.DC_LIC_AUTH) {
        if (typeof (option.selectedLov) == 'string' && option?.list && this.form.get('licenseIssuingAuthority')?.value) {
          this.selectedLicenseAuthority = option?.list.find((lov: any) => lov['Value'].toLowerCase() == this.form.get('licenseIssuingAuthority')?.value.toLowerCase())
        } else {
          this.selectedLicenseAuthority = option?.selectedLov;
        }
        if (this.srSubType == ServiceName.MEMBERSHIP_RENEW) {
          if (this.selectedLicenseAuthority && this.selectedLicenseAuthority["Low"] ==
            "Freezone") {
            this.toggleRequired(true, ['membershipYear']);
          } else {
            if (this.showActions) {
              this.form.get('expiryDate.year')?.enable();
            }
          }
        }
      }
    }
  }

  // Add existing activity to list
  // This method is used to add aactivity using items
  addAactivity(items: any) {
    if (items.length > 0) {
      this.selectedActivities = items;
      this.form.get('activitySearch')?.setValue('selected');
      this.form.get('activitySearch')?.updateValueAndValidity();
    }
  }

  // This method is used to remove activity
  masterActivities: any = [];
  removeActivity(items: any) {
    this.selectedActivities = items;

    // Ensure masterActivities is initialized
    // this.masterActivities = this.masterActivities || [];

    // items.forEach((item: any) => {
    //   const existingIndex = this.masterActivities.findIndex(
    //     (m: any) =>
    //       m.id === item.id && m.code === item.code
    //   );

    //   if (existingIndex === -1) {
    //     //Not found — add as new
    //     this.masterActivities.push(item);
    //   } else if (item?.endDate) {
    //     //Found — update existing with endDate (if provided)
    //     this.masterActivities[existingIndex] = {
    //       ...this.masterActivities[existingIndex],
    //       endDate: item.endDate,
    //     };
    //   }
    // });

    // // Exclude items that have an endDate
    // this.selectedActivities = this.masterActivities.filter(
    //   (item: any) => !item.endDate
    // );

    if (this.selectedActivities.length < 1) {
      this.form.get('activitySearch')?.setValue(null);
    }
  }

  clearActivity(value: any) {
    if (this.selectedActivities.length > 0) {
      this.form.get('activitySearch')?.setValue('selected');
      this.form.get('activitySearch')?.updateValueAndValidity();
    } else {
      this.form.get('activitySearch')?.setValue(null);
    }
  }

  activityRecord: any;
  // This method is used to open add activity form using activity
  openAddActivityForm(activity: any) {
    this.addActivityModal = true;
    this.activityRecord = activity;
  }

  // This method is used to save new activity activity to list
  saveNewActivity(activityValue: any) {
    const existingOthers = this.selectedActivities.filter((a: any) =>
      /^others?\d*$/i.test(a.code)
    );
    const usedNumbers = existingOthers.map((a: any) => {
      const match = a.code.match(/^others?(\d+)?$/i);
      return match ? (match[1] ? parseInt(match[1], 10) : 0) : 0;
    });

    // Find the next available number
    let nextNumber = 0;
    while (usedNumbers.includes(nextNumber)) {
      nextNumber++;
    }
    // Generate the new code
    const newCode = nextNumber === 0 ? "Others" : `Other${nextNumber}`;

    const formValue = {
      ...activityValue,
      //id: `${activityValue.id}_${newCode}`, // or keep baseValue.id if id is constant
      //code: newCode,
      "DC Special Act Code Eng": activityValue?.descriptionOtherEn ? activityValue.descriptionOtherEn : activityValue.name,
      "DC Special Act Code Ara": activityValue?.descriptionOtherAr ? activityValue.descriptionOtherAr : activityValue.description
    };
    const existingIndex = this.selectedActivities.findIndex((o: any) => o.id === formValue.id);
    if (existingIndex !== -1) {
      this.selectedActivities[existingIndex] = formValue;
    } else {
      const newValue = {
        ...activityValue,
        id: `${activityValue.id}_${newCode}`, // or keep baseValue.id if id is constant
        code: newCode,
        "DC Special Act Code Eng": activityValue.name,
        "DC Special Act Code Ara": activityValue.description
      };
      this.selectedActivities.push(newValue);
    }
    this.form.get('activitySearch')?.setValue('selected');
    this.form.get('activitySearch')?.updateValueAndValidity();
  }



  // This method is used to handle file upload using file
  handleFileUpload(file: any) { }
  // This method is used to handle file remove using file
  handleFileRemove(file: any) {
    this.filesList = this.filesList.filter((f: any) => f !== file);
  }


  // This method is used to open add owner modal
  openAddOwnerModal() {
    const formFieldData = this.form.getRawValue();
    if (this.srNumber == "" && this.type != 'registration') {
      this.submitSRData(formFieldData, SrActionType.DRAFT);
    }
    this.selectedOwner = "";
    this.addOwnerModal = true;
  }
  selectedOwner: any = "";
  masterOwnerList: any = [];
  // This method is used to owner view remove using owner
  ownerViewRemove(owner: any) {
    const formFieldData = this.form.getRawValue();
    if (this.srNumber == "" && this.type != 'registration') {
      this.submitSRData(formFieldData, SrActionType.DRAFT);
    }
    this.openFolder();
    if (owner.type == 'edit') {
      this.selectedOwner = owner.data;
      this.addOwnerModal = true;
    } else {
      const index = this.ownerList.findIndex(
        (o: any) => o.ownerId === owner.data.ownerId
      );

      if (index !== -1) {
        const hasAccountMembershipId = !!this.ownerList[index]?.accountMembershipId;
        if (owner.type === 'activate') {
          const reActivatedOwner = { ...this.ownerList[index], endDate: null };
          this.ownerList[index] = reActivatedOwner;
          this.masterOwnerList = (this.masterOwnerList || []).filter(
            (o: any) => o.ownerId !== reActivatedOwner.ownerId
          );
        } else if (hasAccountMembershipId) {
          // Persisted owner: set endDate instead of removing
          const updatedOwner = {
            ...this.ownerList[index],
            endDate: new Date(),
          };
          this.ownerList[index] = updatedOwner;

          const masterIndex = this.masterOwnerList?.findIndex(
            (o: any) => o.ownerId === updatedOwner.ownerId
          );

          if (masterIndex !== -1) {
            this.masterOwnerList[masterIndex] = updatedOwner;
          } else {
            this.masterOwnerList.push(updatedOwner);
          }
          // If remove existing owner/partner then need to upload memorandom of association
          this.removeMemorandumDocIfNeeded();
        } else {
          // New owner without account membership id: remove entirely
          this.ownerList = this.ownerList.filter((o: any) => o.ownerId !== owner.data.ownerId);
          this.masterOwnerList = (this.masterOwnerList || []).filter(
            (o: any) => o.ownerId !== owner.data.ownerId
          );
        }
      }
      this.updateOwnersControlState();
      this.showShareMsg = this.ownerTotalShare != null &&
        (this.ownerTotalShare < 100 || this.ownerTotalShare > 100);
    }
  }
  // This method is used to keep owners control and flags in sync
  private updateOwnersControlState() {
    const activeOwners = this.activeOwners;
    this.form.get('owners')?.setValue(activeOwners);
    this.form.get('owners')?.updateValueAndValidity();
    this.isOwnerAdded = (this.ownerList || []).length > 0;
  }
  showShareMsg: boolean = false;
  // This method is used to handle owner submit event using owner
  onOwnerSubmit(owner: any) {
    this.isOwnerAdded = true;
    this.showShareMsg = false;
    const existingIndex = this.ownerList.findIndex((o: any) => o.ownerId === owner.ownerId);
    if (this.ownerList.length > 0 && existingIndex !== -1) {
      this.ownerList[existingIndex] = owner;
      if (owner?.accountMembershipId) {
        this.removeMemorandumDocIfNeeded();
      }
    } else {
      owner["ownerId"] = this.ownerList.length + 1;
      this.ownerList.push(owner);
      this.removeMemorandumDocIfNeeded();
    }

    this.updateOwnersControlState();
    if (this.ownerTotalShare != null && this.ownerTotalShare < 100) {
      this.showShareMsg = true;
    }
  }

  get activeOwners() {
    return (this.ownerList || []).filter((owner: any) => !owner.endDate);
  }

  // Clear Memorandum of Association upload so user re-uploads after owner change on amend/renew
  private removeMemorandumDocIfNeeded(): void {
    if (this.srSubType !== ServiceName.MEMBERSHIP_AMENDMENT && this.srSubType !== ServiceName.MEMBERSHIP_RENEW) {
      return;
    }
    const documents = this.srFolder?.documents;
    const hasExistingMoaInSr = MembershipSharedService.hasMoaInSr(this.srFolder);

    // If SR already has an MOA uploaded, do not clear anything.
    if (hasExistingMoaInSr) {
      return;
    }

    // Flag to remove MOA from member docs when they are fetched/merged
    this.shouldClearMoaFromMemberDocs = true;

    if (Array.isArray(documents)) {
      const filteredDocuments = documents.filter((doc: any) => !MembershipSharedService.isMoaType(doc?.documentType));
      if (filteredDocuments.length !== documents.length) {
        this.srFolder = {
          ...(this.srFolder ?? {}),
          documents: filteredDocuments
        };
      }
    }

    if ((this.memberDocuments || []).length > 0) {
      const filteredMemberDocs = this.memberDocuments.filter((doc: any) => !MembershipSharedService.isMoaType(doc?.documentType));
      if (filteredMemberDocs.length !== this.memberDocuments.length) {
        this.memberDocuments = filteredMemberDocs;
        // MOA was cleared now; no need to keep the flag
        this.shouldClearMoaFromMemberDocs = false;
      }
    }
  }

  // This method is used to return owner/partner total share
  get ownerTotalShare() {
    const hasSharesOwner = this.activeOwners.some((owner: any) =>
      (owner.roles || []).some((role: string) => role.trim() === 'Shares Owner')
    );

    if (!hasSharesOwner) {
      return null; // or null or '--' based on your UI requirement
    }

    return this.activeOwners.reduce((sum: number, owner: any) => {
      return sum + Number(owner.share || 0);
    }, 0);
  }

  // This method is used to move to the next step
  nextStep(): void {
    // this.isTradeLicenseValid = true; 
    // if (this.isTradeLicenseValid == false) {
    //   this.validateTradeLicense();
    //   return;
    // }
    const hasSharesOwner = this.activeOwners?.some((owner: any) =>
      (owner.roles || []).includes('Shares Owner')
    );
    const isShareValid = !hasSharesOwner || this.ownerTotalShare === 100;

    const isParentCompanyValid = this.form.get('hasParentCompany')?.value !== 'Yes' ||
      (!this.memberLicenseError && this.isValidMemberNumber);

    if (this.textFields && this.optionFields) {
      this.allFields = [...this.textFields.toArray(), ...this.optionFields.toArray(), ...this.mobileFields.toArray(), ...this.dateFields.toArray()];
    }

    const ownerStepFields = this.isOffshoreLicenseType ? [] : ['hasParentCompany', 'owners', 'parentCompanyLicenceNumber', 'parentCompanyMemberNumber'];
    const activityStepFields = this.isOffshoreLicenseType
      ? ['paymentMethod']
      : ['activitySearch', 'activitiesConfirmed', 'parentCompanyLicenceNumber', 'parentCompanyMemberNumber', 'paymentMethod'];
    const parentCompanyFields = this.isOffshoreLicenseType ? [] : ['parentCompanyLicenceNumber', 'parentCompanyMemberNumber'];

    const stepFieldsMap: { [key: number]: string[] } = {
      1: ['licenceType', 'licenseIssuingAuthority', 'licenceNumber'],
      2: ['tradeNameEn',
        'tradeNameAr',
        'issueDate.day',
        'issueDate.month',
        'issueDate.year',
        'expiryDate.day',
        'expiryDate.month',
        'expiryDate.year',
        'nationality',
        'legalStatus',
        'firmType',
        'legalName', 'legalEmail', 'legalContactCountryCode', 'legalContactNumber', 'legalMemberNumber',
        'decreeNumber', 'decreeIssueDate.day', 'decreeIssueDate.month', 'decreeIssueDate.year',
        'officeAddress',
        'buildingName',
        'street',
        'address',
        'countryCode',
        'phoneNumber',
        'contactEmail',
        'membershipYear'
      ],
      3: ownerStepFields,
      4: activityStepFields,
      5: parentCompanyFields,
      6: parentCompanyFields
    };

    if (this.step > 2) {
      this.isFromSumit = true;
    }

    if (this.selectedActivities && this.selectedActivities.length > 0) {
      this.form.get('activitySearch')?.setValue('selected');
      this.form.get('activitySearch')?.updateValueAndValidity();
    }

    if (!environment.ignoreValidators) {
      // Validate all steps up to the current step
      for (let i = 1; i <= this.step; i++) {
        const fieldsToCheck = stepFieldsMap[i] || [];
        if (Utils.handleInvalidStep(this.form, this.allFields, fieldsToCheck)) {
          // If invalid, set current step to the one with the error
          // this.step = i;
          const invalidControls = Utils.getInvalidControls(this.form);
          this.form.markAllAsTouched();
          console.warn('Invalid controls:', invalidControls);
          return;
        }
      }
    }

    let formData = this.form.getRawValue();
    if (this.shouldShowStep2DraftConfirmModal()) {
      this.pendingStep2DraftFormData = formData;
      this.showStep2DraftConfirmModal = true;
      return;
    }

    if (this.step == 1 && !this.srNumber) {
      this.getLicenseDetail();
    }
    if (this.step == 2) {
      this.submitSRData(formData, SrActionType.DRAFT);
    }
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);



    if (this.step >= 5) {
      formData["owners"] = this.activeOwners;
      formData["activities"] = this.selectedActivities;

      if (this.form.invalid && invalidControls) {
        Utils.focusFirstInvalidControl(this.form, this.allFields);
        this.form.markAllAsTouched();
        return;
      }
      if (!isShareValid || !isParentCompanyValid) {
        Utils.focusFirstInvalidControl(this.form, this.allFields);
        this.form.markAllAsTouched();
        if (!isShareValid) {
          this.showShareMsg = true;
          const target = this.document?.getElementById('owner-list');
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }
      this.submitSRData(formData, SrActionType.SUBMIT);
    }
    if (this.memberLicenseError && this.licenseMember['Error Code'] == 1) {
      return;
    }
    if (this.step < 5) {
      if (this.step >= 2 && this.isOffshoreLicenseType) {
        this.step = 5;
      } else {
        this.step++;
      }
    }
  }

  isFromSumit: boolean = false;
  // This method is used to save and close
  saveAndClose(): void {
    const formFieldData = this.form.getRawValue();
    if (this.step == 1 && !this.srNumber) {
      this.getLicenseDetail();
      return;
    }

    if (!this.isTradeLicenseValid) {
      return
    }
    const isWithoutLicense = formFieldData.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE ||
      formFieldData.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE_AR;

    const isOffshore = formFieldData.licenceType == FieldValue.OFFSHORE_LICENSE ||
      formFieldData.licenceType == FieldValue.OFFSHORE_LICENSE_AR;

    const licenceNumberRequired = !isWithoutLicense || isOffshore;

    if (formFieldData.licenseIssuingAuthority &&
      (!licenceNumberRequired || formFieldData.licenceNumber)) {

      // Show confirmation for license number and authority
      if (this.shouldShowStep2DraftConfirmModal()) {
        this.pendingStep2DraftFormData = formFieldData;
        this.showStep2DraftConfirmModal = true;
        this.isOpenFromSaveAndClose = true;
        return;
      } else {
        this.submitSRData(formFieldData, SrActionType.SAVE);
      }
    } else {
      this.form.markAllAsTouched();
      if (licenceNumberRequired) {
        this.form.get('licenceNumber')?.markAsTouched();
      }
      return
    }
  }
  calculateOffshoereMembershipExpiryDate(data: any) {
    let updatedExpiryDate: any = "";
    let licenseIssueDate = data.issueDate.day ?
      `${data.issueDate.month}/${data.issueDate.day}/${data.issueDate.year}` : '';

    let licenseExpiryDate = data.expiryDate.day ?
      `${data.expiryDate.month}/${data.expiryDate.day}/${data.expiryDate.year}` : '';

    let decreeIssueDate = data.decreeIssueDate?.day ?
      `${data.decreeIssueDate.month}/${data.decreeIssueDate.day}/${data.decreeIssueDate.year}` : '';

    const isOffshore = data.licenceType == FieldValue.OFFSHORE_LICENSE || data.licenceType == FieldValue.OFFSHORE_LICENSE_AR;
    const isWithoutLicense = data.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE || data.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE_AR;

    const baseDateString = decreeIssueDate || licenseIssueDate;
    const shouldCalculate = (isOffshore || isWithoutLicense) && !!baseDateString;

    if (shouldCalculate) {
      let issueDateObj: any;
      if (isWithoutLicense) {
        issueDateObj = new Date();
      } else {
        issueDateObj = new Date(baseDateString);
      }

      if (isNaN(issueDateObj.getTime())) {
        return updatedExpiryDate;
      }

      // Add 365 days
      issueDateObj.setDate(issueDateObj.getDate() + 365);

      const month = String(issueDateObj.getMonth() + 1).padStart(2, '0');
      const day = String(issueDateObj.getDate()).padStart(2, '0');
      const year = issueDateObj.getFullYear();
      updatedExpiryDate = `${month}/${day}/${year}`;

      let newExpriyDate: any;
      if (isOffshore) {
        //for offshore membership expiry date will be == license expiry date
        updatedExpiryDate = licenseExpiryDate;
        newExpriyDate = updatedExpiryDate.split('/');
      } else {
        newExpriyDate = updatedExpiryDate.split('/');
      }

      this.form.get("membershipExpiryDate")?.patchValue({
        day: newExpriyDate ? newExpriyDate[1] : null,
        month: newExpriyDate ? newExpriyDate[0] : null,
        year: newExpriyDate ? Number(newExpriyDate[2].toString()) : null
      });
    }
    return updatedExpiryDate;
  }

  formSubmitLoader: boolean = false;
  private isSrSubmitInProgress: boolean = false;
  ownerFieldError: any = "";
  errorMsg: string = "";
  technicalErrorMsg: boolean = false;
  // This method is used to submit sr data using data and action type
  submitSRData(data: any, actionType: string) {
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);
    this.errorMsg = "";
    this.technicalErrorMsg = false;
    this.ownerFieldError = "";
    if (!this.isTradeLicenseValid) {
      return
    }
    let isActionSubmit = actionType == SrActionType.SUBMIT;
    let isActionSave = actionType == SrActionType.SAVE;
    let isActionDraft = actionType == SrActionType.DRAFT;
    const defaultShowActions = this.showActions;
    const restoreFormState = () => {
      this.showActions = defaultShowActions;
      // this.form.enable({ emitEvent: false });
      this.lockTradeLicenseFieldsForNewMembership();
      this.formSubmitLoader = false;
    };
    if (this.isSrSubmitInProgress) {
      return;
    }

    if (Utils.hasMaxLengthError(this.form)) {
      this.form.markAllAsTouched();
      return;
    }

    //check documents upload or not in final submit
    if (isActionSubmit && this.checkRequiredDocuments()) {
      this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
      return;
    }

    if (isActionSubmit) {
      const missingRequiredControls = this.getMissingRequiredControls(this.form);
      if (missingRequiredControls.length > 0) {
        this.markControlsAsTouched(missingRequiredControls);
        this.focusFirstMissingRequiredControl(missingRequiredControls);
        this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
        return;
      }
    }

    let licenseExpiryDate = data.expiryDate.day ?
      `${data.expiryDate.month}/${data.expiryDate.day}/${data.expiryDate.year}` : '';
    let licenseIssueDate = data.issueDate.day ?
      `${data.issueDate.month}/${data.issueDate.day}/${data.issueDate.year}` : '';

    //calculate membership expiry date
    let isFreeZone = (this.selectedLicenseAuthority && this.selectedLicenseAuthority["Low"] == "Freezone") ? true : false;
    let membershipExpiryDate: any = "";
    if (licenseIssueDate && licenseExpiryDate) {
      membershipExpiryDate = this.calculateMembershipExpiryDate(licenseIssueDate, licenseExpiryDate, isFreeZone);
    }

    // membership expiry date for offshore license and without license
    if ((data.licenceType == FieldValue.OFFSHORE_LICENSE || data.licenceType == FieldValue.OFFSHORE_LICENSE_AR)
      || (data.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE || data.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE_AR)) {
      membershipExpiryDate = this.calculateOffshoereMembershipExpiryDate(data) ?? membershipExpiryDate;
    }

    let srOwners: any = [];
    let srActivities: any = [];
    //Count all "Shares Owner" roles
    const totalOwners = data["owners"]?.filter((o: any) =>
      o.roles?.includes("Shares Owner")
    )?.length || 0;

    // This method is used to format date
    const formatDate = (input: string | Date): string => {
      const date = input instanceof Date ? input : new Date(input);

      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    };

    //Process each owner
    let finalOwners: any = this.getOwnerPartnerToSubmit(data["owners"]);
    finalOwners?.forEach((owner: any) => {
      const roles = owner.roles?.map((r: string) => r.trim()) || [];
      const hasManager = roles.includes('Manager');
      const hasOwner = roles.includes('Shares Owner');
      const hasAuthSig = roles.includes('Authorised Signatory');

      // Set isAuthorisedSignatory flag
      const isAuthorisedSignatory = hasAuthSig ? 'Y' : 'N';

      // Set Job Title (based on the first role in priority order)
      let jobTitle = '';
      if (hasOwner) jobTitle = 'Owner';
      if (hasManager) jobTitle = 'Manager';
      if (hasOwner && hasManager) jobTitle = 'Manager';
      if (hasAuthSig && !hasOwner && !hasManager) jobTitle = 'Authorised Signatory';

      // Determine relationType
      let relationType = 'Owner';
      if (hasOwner) {
        // relationType = totalOwners > 1 ? 'Partner' : 'Owner';
        relationType = "Owner"; //09-25 change as per new requirement, always set Owner
      } else if (hasManager || hasAuthSig) {
        relationType = 'Contact Of';
      } else {
        if (!hasOwner && !hasManager) {
          relationType = totalOwners > 1 ? 'Partner' : 'Contact Of';
          jobTitle = totalOwners > 1 ? 'Manager' : 'Owner';
        }
      }

      // Edge case: if share % < 100, relationship should be Partner
      // if (hasOwner && this.ownerTotalShare < 100) {
      //   relationType = 'Partner';
      // }
      let currentYear: any = new Date().getFullYear();
      let emiratesDob = (
        owner?.emiratesDob?.day &&
        owner?.emiratesDob?.month &&
        owner?.emiratesDob?.year &&
        !isNaN(Number(owner.emiratesDob.day)) &&
        !isNaN(Number(owner.emiratesDob.month)) &&
        !isNaN(Number(owner.emiratesDob.year))
      )
        ? `${owner.emiratesDob.month}/${owner.emiratesDob.day}/${owner.emiratesDob.year}`
        : '';
      let passportExpiry = (
        owner?.passportExpiry?.day &&
        owner?.passportExpiry?.month &&
        owner?.passportExpiry?.year &&
        !isNaN(Number(owner.passportExpiry.day)) &&
        !isNaN(Number(owner.passportExpiry.month)) &&
        !isNaN(Number(owner.passportExpiry.year))
      )
        ? `${owner.passportExpiry.month}/${owner.passportExpiry.day}/${owner.passportExpiry.year}`
        : '';

      srOwners.push({
        "Relationship": relationType,
        "DC Main Phone": `${owner.countryCode}${owner.mobile}`,
        "Member DOB": emiratesDob,
        "DC Emirates ID Number": owner.emiratesId ? owner.emiratesId : "",
        "DC National ID": null,
        "DC Ownership Percent": owner.share,
        "DC Relationship Type": owner.partnerType == "Company" ? 'Organization' : 'Contact',
        "DC Member Relationship": relationType,
        "DC PP Exp Date": passportExpiry,
        "UAE Citizen": owner.isResident && (owner.isResident == FieldValue.YES || owner.isResident == FieldValue.YES_AR) ? "Yes" : 'No',
        "DC Contact Name(ARABIC)": owner.nameAr ? owner.nameAr.trim() : '',
        "DC Contact Name(ENGLISH)": owner.nameEn ? owner.nameEn.trim() : '',
        "DC Name(ENGLISH)": owner.companyNameEn ? owner.companyNameEn.trim() : '',
        "DC Name(ARABIC)": owner.companyNameAr ? owner.companyNameAr.trim() : '',
        "DC Cell Phone": owner.countryCode && owner.mobile ? `${owner.countryCode}${owner.mobile}` : "",
        "Membership Year": currentYear.toString(),
        "DC DED Partner Passport Number": owner.passportNumber ? owner.passportNumber : '',
        "DC Contact Nationality": owner.nationality,
        "DC Email Address": owner.email,
        "DC DED PartnerNationalityCode": "",//"UAE",
        "DC Authorized Signatory": isAuthorisedSignatory,
        "DC Job Title": jobTitle,
        "DC End Date": owner.endDate ? formatDate(owner.endDate) : "",
        "Account Membership Id": owner.accountMembershipId ? owner.accountMembershipId : ""
      })
    })

    let finalActivities: any = this.getActivitiesToSubmit();

    finalActivities?.map((activity: any) => {
      if (activity.code) {
        let activityObj: any = {
          // "Name": activity.name,
          "DC Activity Fee": activity.codeFee,
          "DC Activity Description": activity.name, //activity.description,
          "DC Activity Description Arabic": activity.description,
          "DC Activity Code": activity.code,
          "DC End Date": activity.endDate ? formatDate(activity.endDate) : "",
          "Account Activity Id": activity.accountActivityId ? activity.accountActivityId : ""
        }
        if (activity["DC Special Act Code Ara"] && activity["DC Special Act Code Eng"]) {
          // activityObj.Name = "";
          activityObj["DC Activity Description"] = "";
          activityObj["DC Activity Description Arabic"] = "";
          activityObj["DC Special Act Code Eng"] = activity["DC Special Act Code Eng"];
          activityObj["DC Special Act Code Ara"] = activity["DC Special Act Code Ara"];
        }
        if (activity["descriptionOtherEn"] && activity["descriptionOtherAr"]) {
          // activityObj.Name = "";
          activityObj["DC Activity Description"] = "";
          activityObj["DC Activity Description Arabic"] = "";
          activityObj["DC Special Act Code Eng"] = activity["descriptionOtherEn"];
          activityObj["DC Special Act Code Ara"] = activity["descriptionOtherAr"];
        }
        srActivities.push(activityObj);
      }
    })
    let address: any = "";

    if (data.street) {
      address = {
        "Apartment Number": data.buildingName,
        "DC PO Box": "",
        "Descriptor": data.officeAddress,
        //"City": "DUBAI",
        "Country": this.lang == 'ar' ? FieldValue.UAE_AR : FieldValue.UAE,
        "DC Address English Name": data.address,
        "Street Address": data.street,
        "DC Address Arabic Name": "",
        "Primary Address Id": data.primaryAddressId ? data.primaryAddressId : ""
      }
    }
    const membershipYearsForRenew = (() => {
      if (this.srSubType !== ServiceName.MEMBERSHIP_RENEW) {
        return "";
      }
      const isFreeZone = this.isFreeZoneMembershipRenewal();
      if (isFreeZone) {
        return this.normalizeMembershipYearsValue(data.membershipYear);
      }

      if (!licenseExpiryDate || !this.selectedMemberExpiryDate) {
        return "";
      }

      const toDate = (value: any): Date | null => {
        if (!value) return null;
        if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
        const parts = `${value}`.split('/');
        if (parts.length === 3) {
          const month = Number(parts[0]);
          const day = Number(parts[1]);
          const year = Number(parts[2]);
          const parsed = new Date(year, month - 1, day);
          return isNaN(parsed.getTime()) ? null : parsed;
        }
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
      };

      const selectedExpiryDate = toDate(this.selectedMemberExpiryDate);
      const licenseExpiryDateObj = toDate(licenseExpiryDate);
      if (!selectedExpiryDate || !licenseExpiryDateObj) {
        return "";
      }

      const diffInMs = licenseExpiryDateObj.getTime() - selectedExpiryDate.getTime();
      const diffInYears = Math.abs(Math.round(diffInMs / (1000 * 60 * 60 * 24 * 365)));
      return this.normalizeMembershipYearsValue(diffInYears);
    })();

    const isOffshore = data.licenceType == FieldValue.OFFSHORE_LICENSE || data.licenceType == FieldValue.OFFSHORE_LICENSE_AR;
    const isWithoutLicense = data.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE || data.licenseIssuingAuthority == FieldValue.WITHOUT_LICENSE_AR;
    const hasInvalidRenewMembershipYears = this.srSubType === ServiceName.MEMBERSHIP_RENEW &&
      !isWithoutLicense &&
      this.isInvalidMembershipYearsValue(membershipYearsForRenew);

    if (isActionSubmit && hasInvalidRenewMembershipYears) {
      this.sharedService.toastMessage('ERROR', "INVALID_MEMBERSHIP_YEAR", 'error', false);
      const target = this.document?.getElementById('expiry_dt');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const validationErrors = this.validateSrOwners(srOwners);
    let requestPayload: any = {
      "body": {
        "ProcessName": "DC Create Update SR Web WF",
        "DCMethod": isActionSubmit ? 'SUBMIT' : 'SAVEASDRAFT',
        "SiebelMessage": {
          "xmlns": "http://www.siebel.com/xml/DC%20Query%20Member%20Info",
          "MessageId": Utils.generateUniqueId(),
          "MessageType": "Integration Object",
          "IntObjectName": SrIOName.MEMBERSHIP,
          "IntObjectFormat": "Siebel Hierarchical",
          "ListOfDCMemSRIO": {
            "DC Mem SR": {
              "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
              "DC Payment Method": data.paymentMethod ? data.paymentMethod : '',
              "SR Sub Type": this.srSubType,
              "SR Type": ServiceName.MEMBERSHIP,
              "DC Legal Status": data.legalStatus,
              "DC License Expiry Date": licenseExpiryDate,  //"06/30/2026",
              "DC License Registration Number": data.licenceNumber ?? '',
              "Parent Comp Lic Number": data.hasParentCompany == "Yes" && data.parentCompanyLicenceNumber ? data.parentCompanyLicenceNumber : "",
              "DC License Issue Date": licenseIssueDate,
              "DC Respondent Country": data.nationality,
              "DC Member Name(Arabic)": data.tradeNameAr?.trim() ?? '',
              "DC License Issuing Authority": data.licenseIssuingAuthority,
              "DC Holder Phone": data.phoneNumber && data.countryCode ? `${data.countryCode}${data.phoneNumber}` : "",
              "DC Member Name(English)": data.tradeNameEn?.trim() ?? '',
              "Parent Comp Mem Num": data.hasParentCompany == "Yes" && data.parentCompanyMemberNumber ? data.parentCompanyMemberNumber : '',
              "DC CRB Primary Email": data.contactEmail,
              "DC License Type": data.licenceType,
              "Parent Company": data.hasParentCompany ? data.hasParentCompany : 'No',
              "Membership Years": membershipYearsForRenew,
              "DC Firm Type": data.firmType,
              "DC Membership Expiry Date": membershipExpiryDate,  //"06/30/2026",
              "Parent Company Name English": this.licenseMember ? this.licenseMember?.MemberName.trim() : "",
              "Parent Company Name Arabic": this.licenseMember ? this.licenseMember?.MemberNameAra.trim() : "",
              "Parent Company Expiry Date": this.licenseMember ? formatDate(this.licenseMember?.ExpiryDate) : "",
              "ListOfDC Mem SR Relationship": {
                "DC Mem SR Relationship": validationErrors && validationErrors.length > 0 && this.srSubType == ServiceName.MEMBERSHIP_NEW ? "" : (srOwners && srOwners.length > 0 ? srOwners : "")
              },
              "ListOfDC Mem SR Activity Code": {
                "DC Mem SR Activity Code": srActivities && srActivities.length > 0 ? srActivities : ""
              },
              "ListOfDC Mem SR CUT Address": {
                "DC Mem SR CUT Address": address ? address : ""
              },
              "DC Decree Issue Date": data.decreeIssueDate.day ?
                `${data.decreeIssueDate.month}/${data.decreeIssueDate.day}/${data.decreeIssueDate.year}` : '',
              "DC Decree Number": data.decreeNumber ?? '',
              "DC Legal Contact": data.legalContactNumber && data.legalContactCountryCode ? `${data.legalContactCountryCode}${data.legalContactNumber}` : "",
              "DC Legal Email Addr": data.legalEmail ?? '',
              "DC Legal Member Number": data.legalMemberNumber ?? '',
              "DC Legal Name": data.legalName ?? '',
            }
          }
        }
      }
    }

    const isNewRegistration = this.srSubType === ServiceName.MEMBERSHIP_NEW && this.type === 'registration';
    const isNonMemberUser = this.userType !== UserType.MEMBER;

    // For registration
    if (isNewRegistration && !this.srNumber) {
      this.srAccountId = "";
      delete requestPayload.body.SiebelMessage.ListOfDCMemSRIO['DC Mem SR']["Account Id"];
    }

    // Cross-validate: non-member new registration must not carry Account Id before SR is created
    if (isNewRegistration && isNonMemberUser && !this.srNumber) {
      this.srAccountId = "";
      delete requestPayload.body.SiebelMessage.ListOfDCMemSRIO['DC Mem SR']["Account Id"];
    }

    if (this.srNumber && this.type == 'registration' && this.srAccountId) {
      requestPayload.body.SiebelMessage.ListOfDCMemSRIO['DC Mem SR']["SR Number"] = this.srNumber;
      requestPayload.body.SiebelMessage.ListOfDCMemSRIO['DC Mem SR']["Account Id"] = this.srAccountId;
    }

    // For amendment and renew send account id
    if (this.type == 'amendment' || this.type == 'renew') {
      requestPayload.body.SiebelMessage.ListOfDCMemSRIO['DC Mem SR']["Account Id"] = this.srAccountId;
      if (this.srNumber) {
        requestPayload.body.SiebelMessage.ListOfDCMemSRIO['DC Mem SR']["SR Number"] = this.srNumber;
      }
    }
    //Check owner fields value
    // const validationErrors = this.validateSrOwners(srOwners);
    if (validationErrors.length > 0 && (isActionSubmit || isActionSave)) {
      this.ownerFieldError = validationErrors;
      const target = this.document?.getElementById('owner-list');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (!this.isOffshoreLicenseType) {
      if (this.selectedActivities?.length < 1 && isActionSubmit) {
        this.form.get('activitySearch')?.setValue('');
        return;
      }
    }

    //Added check for check membership expiry and license expiry date should be future date
    const isRenewSubmit =
      isActionSubmit &&
      (this.type === 'renew' || this.srSubType === ServiceName.MEMBERSHIP_RENEW);
      
      if (
        data.licenseIssuingAuthority != FieldValue.WITHOUT_LICENSE 
        && data.licenseIssuingAuthority != FieldValue.WITHOUT_LICENSE_AR
      ) {
        if (isRenewSubmit && !this.hasValidRenewFutureExpiryDates(licenseExpiryDate, membershipExpiryDate, true)) {
          return;
        }
      }

    //Additional validation for parent company license and member number
    if (this.isSameAsCurrentParentCompanyDetails()) {
      this.form.get('parentCompanyLicenceNumber')?.setValue('');
      this.form.get('parentCompanyMemberNumber')?.setValue('');
      this.resetParentCompanyValidationState();
      this.memberLicenseError = true;
      this.sharedService.toastMessage('ERROR', 'PARENT_COMPANY_MEMBER_MESSAGE', 'error', true);
      return;
    }
    this.formSubmitLoader = !isActionDraft ? true : false;
    this.isSrSubmitInProgress = true;
    if (!isActionDraft) {
      this.showActions = false;
    }
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.CREATE_UPDATE_SR,
      requestPayload
    ).subscribe({

      next: (response: any) => {
        if ((response['Error Code'] == "" || response['Error Code'] == "DC-VAL-ERR")
          && response["Error Message"] == null || response["Error Message"] == ""
          && response["SR Number"]) {
          this.isSaved = true;

          if (isActionSave || isActionDraft) {
            if (isActionDraft && this.srNumber == "") {
              this.srNumber = response["SR Number"];
              this.srAccountId = response['Account Id'];
              sessionStorage.setItem("srNumber", response["SR Number"]);
              this.openFolder();
            } else {
              this.srNumber = response["SR Number"];
              this.srAccountId = response['Account Id'];
              if (isActionSave) {
                this.successModal = true;
              }
            }
          } else {
            //set sr detail for success page to get ioname
            this.srNumber = response["SR Number"];
            const { service, serviceAr } = Utils.getServiceLabels(this.srSubType, this.lang);
            let srDetailObj = {
              serviceSubTypeEn: service,
              serviceSubTypeAr: serviceAr,
              service: this.srSubType,
              IOName: SrIOName.MEMBERSHIP,
              srNumber: this.srNumber,
              totalSteps: this.totalSteps,
              step: this.step
            }
            this.sharedService.setSrResponse(srDetailObj);

            this.sharedService.toastMessage('SUCCESS', 'REQUEST_SUBMITTED', 'success');
            sessionStorage.removeItem("srNumber");
            this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.SUCCESS_PAGE + "/" + this.srNumber}`]);
          }
          restoreFormState();
          this.isSrSubmitInProgress = false;
        } else {
          restoreFormState();
          this.isSrSubmitInProgress = false;
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != "") {
            this.errorMsg = response['Error Message'];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      },
      // This method is used to handle the error callback using e
      error: (e) => {
        this.isSaved = true;
        restoreFormState();
        this.technicalErrorMsg = true;
        this.isSrSubmitInProgress = false;
      }
    })
  }

  private isFreeZoneMembershipRenewal(): boolean {
    return this.selectedLicenseAuthority?.["Low"] == "Freezone" || this.licenseCategory == "Freezone";
  }

  private getMissingRequiredControls(
    control: AbstractControl,
    pathPrefix: string = ''
  ): string[] {
    if (control instanceof FormGroup) {
      return Object.keys(control.controls).flatMap((key) => {
        const childPath = pathPrefix ? `${pathPrefix}.${key}` : key;
        return this.getMissingRequiredControls(control.controls[key], childPath);
      });
    }

    if (control instanceof FormArray) {
      return control.controls.flatMap((childControl, index) => {
        const childPath = `${pathPrefix}.${index}`;
        return this.getMissingRequiredControls(childControl, childPath);
      });
    }

    const isRequired =
      control.hasValidator?.(Validators.required) ||
      control.hasValidator?.(Validators.requiredTrue);

    if (!isRequired) {
      return [];
    }

    return this.isEmptyRequiredValue(control.value) ? [pathPrefix] : [];
  }

  private isEmptyRequiredValue(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (typeof value === 'boolean') {
      return value !== true;
    }

    return false;
  }

  private markControlsAsTouched(controlPaths: string[]): void {
    controlPaths.forEach((path) => {
      const control = this.form.get(path);
      control?.markAsTouched();
      control?.markAsDirty();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private focusFirstMissingRequiredControl(controlPaths: string[]): void {
    if (!controlPaths.length) {
      return;
    }

    const controlsToFocus = controlPaths
      .map((path) => this.form.get(path))
      .filter((control): control is AbstractControl => !!control);

    const matchingFieldComponent = this.allFields.find((field: any) =>
      controlsToFocus.includes(field.control)
    );

    if (matchingFieldComponent?.focus) {
      matchingFieldComponent.focus();
    }
  }

  private normalizeMembershipYearsValue(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    return `${value}`.trim();
  }

  private isInvalidMembershipYearsValue(value: any): boolean {
    const normalizedValue = this.normalizeMembershipYearsValue(value);
    if (normalizedValue === "") {
      return true;
    }

    const parsedValue = Number(normalizedValue);
    return !Number.isFinite(parsedValue) || parsedValue <= 0;
  }

  // This method is used to validate sr owners using sr owners
  validateSrOwners_old(srOwners: any[]): string[] {
    const errors: string[] = [];
    const requiredFieldsForUAEYes = [
      "Member DOB",
      "DC Emirates ID Number",
      "DC Contact Name(ENGLISH)",
      "DC Contact Name(ARABIC)",
      "DC Cell Phone",
      "DC Email Address",
      "DC Contact Nationality"
    ];

    const requiredFieldsForUAENo = [
      "DC PP Exp Date",
      "DC DED Partner Passport Number",
      "DC Contact Name(ENGLISH)",
      "DC Contact Name(ARABIC)",
      "DC Cell Phone",
      "DC Email Address",
      "DC Contact Nationality"
    ];

    const requiredFieldsForRelationshipType = [
      "DC Name(ENGLISH)",
      "DC Name(ARABIC)",
      "DC Cell Phone",
      "DC Email Address",
      "DC Contact Nationality"
    ];

    // Reset all owners first
    this.ownerList.forEach((o: any) => o.isError = false);

    // Track which owners should be marked as error
    const ownersWithErrors: Set<any> = new Set();

    srOwners.forEach((owner, index) => {
      const ownerIndex = index + 1;
      let hasError = false;

      // UAE Citizen = YES
      if ((owner["UAE Citizen"] === FieldValue.YES || owner["UAE Citizen"] === FieldValue.YES_AR) &&
        owner["DC Relationship Type"] !== 'Organization') {

        requiredFieldsForUAEYes.forEach(field => {
          if (!owner[field]) {
            errors.push(`Owner ${ownerIndex} is missing: ${field}`);
            hasError = true;
          }
        });
      }
      // UAE Citizen = NO
      else if ((owner["UAE Citizen"] === FieldValue.NO || owner["UAE Citizen"] === FieldValue.NO_AR) &&
        owner["DC Relationship Type"] !== 'Organization') {

        requiredFieldsForUAENo.forEach(field => {
          if (!owner[field]) {
            errors.push(`Owner ${ownerIndex} is missing: ${field}`);
            hasError = true;
          }
        });
      }
      // Relationship Type = Organization
      else if (owner["DC Relationship Type"] === 'Organization') {
        requiredFieldsForRelationshipType.forEach(field => {
          if (!owner[field]) {
            errors.push(`Owner ${ownerIndex} is missing: ${field}`);
            hasError = true;
          }
        });
      }

      // Collect all matching owners for error marking
      if (hasError) {
        this.ownerList
          .filter((o: any) =>
            o.nameEn === owner["DC Contact Name(ENGLISH)"] ||
            o.companyNameEn === owner["DC Contact Name(ENGLISH)"] ||
            o.nameEn === owner["DC Name(ENGLISH)"] ||
            o.companyNameEn === owner["DC Name(ENGLISH)"]
          )
          .forEach((o: any) => ownersWithErrors.add(o));
      }
    });

    // Now mark all collected owners as isError = true
    this.ownerList.forEach((o: any) => {
      if (ownersWithErrors.has(o)) {
        o.isError = true;
      }
    });
    return errors;
  }

  validateSrOwners(srOwners: any[]): string[] {
    const errors: string[] = [];

    const requiredFieldsForUAEYes = [
      "Member DOB",
      "DC Emirates ID Number",
      "DC Contact Name(ENGLISH)",
      "DC Contact Name(ARABIC)",
      "DC Cell Phone",
      "DC Email Address",
      "DC Contact Nationality"
    ];

    const requiredFieldsForUAENo = [
      "DC PP Exp Date",
      "DC DED Partner Passport Number",
      "DC Contact Name(ENGLISH)",
      "DC Contact Name(ARABIC)",
      "DC Cell Phone",
      "DC Email Address",
      "DC Contact Nationality"
    ];

    const requiredFieldsForRelationshipType = [
      "DC Name(ENGLISH)",
      "DC Name(ARABIC)",
      "DC Cell Phone",
      "DC Email Address",
      "DC Contact Nationality"
    ];

    // Reset visual errors
    this.ownerList.forEach((o: any) => (o.isError = false));

    const ownersWithErrors: Set<any> = new Set();

    srOwners.forEach((owner, index) => {
      const ownerIndex = index + 1;
      let hasError = false;

      // Skip validation when DC End Date is present (non-null / non-empty).
      const endDateStr = owner["DC End Date"];

      let endDate: Date | null = null;
      if (endDateStr) {
        const [day, month, year] = endDateStr.split("/").map(Number);
        endDate = new Date(year, month - 1, day);
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // If end date exists AND is before today → IGNORE owner
      if (endDateStr != null && `${endDateStr}`.trim() !== '') {
        return; // Skip this owner from validation
      }
      if (endDate && endDate <= today) {
        return; // Skip this owner from validation
      }
      // ================================================================================

      // UAE Citizen = YES
      if (
        (owner["UAE Citizen"] === FieldValue.YES ||
          owner["UAE Citizen"] === FieldValue.YES_AR) &&
        owner["DC Relationship Type"] !== "Organization"
      ) {
        requiredFieldsForUAEYes.forEach((field) => {
          if (!owner[field]) {
            errors.push(`Owner ${ownerIndex} is missing: ${field}`);
            hasError = true;
          }
        });
      }
      // UAE Citizen = NO
      else if (
        (owner["UAE Citizen"] === FieldValue.NO ||
          owner["UAE Citizen"] === FieldValue.NO_AR) &&
        owner["DC Relationship Type"] !== "Organization"
      ) {
        requiredFieldsForUAENo.forEach((field) => {
          if (!owner[field]) {
            errors.push(`Owner ${ownerIndex} is missing: ${field}`);
            hasError = true;
          }
        });
      }
      // Relationship Type = Organization
      else if (owner["DC Relationship Type"] === "Organization") {
        requiredFieldsForRelationshipType.forEach((field) => {
          if (!owner[field]) {
            errors.push(`Owner ${ownerIndex} is missing: ${field}`);
            hasError = true;
          }
        });
      }

      // Mark UI owners with errors
      if (hasError) {
        const contactName = owner["DC Contact Name(ENGLISH)"];
        const orgName = owner["DC Name(ENGLISH)"];
        const matchValues = [contactName, orgName].filter((v) => !!v);

        // Only mark owners that actually match a defined name to avoid flagging all undefined
        if (matchValues.length) {
          this.ownerList
            .filter(
              (o: any) =>
                (matchValues.includes(o.nameEn) || matchValues.includes(o.companyNameEn))
            )
            .forEach((o: any) => ownersWithErrors.add(o));
        }
      }
    });

    // Update UI flags
    this.ownerList.forEach((o: any) => {
      if (ownersWithErrors.has(o)) {
        o.isError = true;
      }
    });

    return errors;
  }


  // This method is used to handle license date change event using event
  onLicenseDateChange(event: any) {
    if (event && event.day && event.month && event.year) {
      this.selectedMemberLicenseExpiryDate = `${event.month}/${event.day}/${event.year}`;
      let data = this.form.getRawValue();

      let licenseExpiryDate = data.expiryDate.day ?
        `${data.expiryDate.month}/${data.expiryDate.day}/${data.expiryDate.year}` : '';
      let licenseIssueDate = data.issueDate.day ?
        `${data.issueDate.month}/${data.issueDate.day}/${data.issueDate.year}` : '';

      let isFreeZone = (this.selectedLicenseAuthority && this.selectedLicenseAuthority["Low"] == "Freezone") ? true : false;

      let membershipExpiryDate: any = "";
      if (licenseIssueDate && licenseExpiryDate) {
        membershipExpiryDate = this.calculateMembershipExpiryDate(licenseIssueDate, licenseExpiryDate, isFreeZone);
        let newExpriyDate = membershipExpiryDate.split('/');
        this.form.get("membershipExpiryDate")?.patchValue({
          day: newExpriyDate ? newExpriyDate[1] : null,
          month: newExpriyDate ? newExpriyDate[0] : null,
          year: newExpriyDate ? Number(newExpriyDate[2].toString()) : null
        });
      }

      // membership expiry date for offshore license and without license
      membershipExpiryDate = this.calculateOffshoereMembershipExpiryDate(data) ?? membershipExpiryDate;

    }
  }

  // This method is used to calculate mem exp date using value
  calculateMemExpDate(option: any) {
    let value: any;
    if (option && !option?.selectedLov) {
      return;
    }
    value = option?.selectedLov.value;
    const noOfYears = parseInt(value, 10);
    let data = this.form.getRawValue();
    if (!data.expiryDate || !data.expiryDate.day) {
      return;
    }
    // Build expiry date object from form data
    let licenseExpiryDate = this.selectedMemberLicenseExpiryDate; //data.expiryDate.day ? `${data.expiryDate.month}/${data.expiryDate.day}/${data.expiryDate.year}` : '';

    let licenseIssueDate = data.issueDate.day ?
      `${data.issueDate.month}/${data.issueDate.day}/${data.issueDate.year}` : '';

    let memExpiryDate = this.selectedMemberExpiryDate; //data.membershipExpiryDate.day ? `${data.membershipExpiryDate.month}/${data.membershipExpiryDate.day}/${data.membershipExpiryDate.year}` : '';

    if (!licenseExpiryDate) return;
    // Convert to Date
    let expiryDate = new Date(licenseExpiryDate);

    // Add years
    expiryDate.setFullYear(expiryDate.getFullYear() + noOfYears);

    this.form.get("expiryDate")?.patchValue({
      day: expiryDate ? expiryDate.getDate().toString().padStart(2, "0") : null,
      month: expiryDate ? (expiryDate.getMonth() + 1).toString().padStart(2, "0") : null,
      year: expiryDate ? expiryDate.getFullYear() : null,
    });

    //calculate membership expiry date
    if (this.srSubType == ServiceName.MEMBERSHIP_NEW) {
      let isFreeZone = (this.selectedLicenseAuthority && this.selectedLicenseAuthority["Low"] == "Freezone") ? true : false;
      let membershipExpiryDate: any = "";
      if (licenseIssueDate && licenseExpiryDate) {
        membershipExpiryDate = this.calculateMembershipExpiryDate(licenseIssueDate, licenseExpiryDate, isFreeZone);
        let newExpriyDate = membershipExpiryDate.split('/');
        this.form.get("membershipExpiryDate")?.patchValue({
          day: newExpriyDate ? newExpriyDate[1] : null,
          month: newExpriyDate ? newExpriyDate[0] : null,
          year: newExpriyDate ? Number(newExpriyDate[2].toString()) : null
        });
      }
    } else {
      let newExpriyDate = new Date(memExpiryDate);
      newExpriyDate.setFullYear(newExpriyDate.getFullYear() + noOfYears);

      this.form.get("membershipExpiryDate")?.patchValue({
        day: newExpriyDate ? newExpriyDate.getDate().toString().padStart(2, "0") : null,
        month: newExpriyDate ? (newExpriyDate.getMonth() + 1).toString().padStart(2, "0") : null,
        year: newExpriyDate ? newExpriyDate.getFullYear() : null,
      });
      // Show error if calculated data is less than today date
      if ((this.checkExpiry(newExpriyDate) || this.checkExpiry(newExpriyDate)) && this.srSubType == ServiceName.MEMBERSHIP_RENEW) {
        this.sharedService.toastMessage(
          "ERROR",
          "CHOOSE_ANOTHER_YEAR",
          "error",
          true
        );
        this.form.get("membershipYear")?.setValue(null);
      }
    }
  }

  checkExpiry(expiryDate: string | Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize today

    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0); // normalize expiry

    return expiry <= today;
  }

  private hasValidRenewFutureExpiryDates(
    licenseExpiryDate: string | Date,
    membershipExpiryDate: string | Date,
    showMessage: boolean = false
  ): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parsedLicenseExpiryDate = this.parseUsDateValue(licenseExpiryDate);
    const parsedMembershipExpiryDate = this.parseUsDateValue(membershipExpiryDate);
    const isValid =
      !!parsedLicenseExpiryDate &&
      !!parsedMembershipExpiryDate &&
      parsedLicenseExpiryDate > today &&
      parsedMembershipExpiryDate > today;

    if (!isValid && showMessage) {
      this.sharedService.toastMessage(
        'ERROR',
        'SELECT_CORRECT_MEMBERSHIP_YEAR_OR_FUTURE_YEAR',
        'error',
        false
      );
      const target = this.document?.getElementById('expiry_dt');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    return isValid;
  }

  private parseUsDateValue(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const parts = String(value).split('/');
    if (parts.length !== 3) {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    const month = Number(parts[0]);
    const day = Number(parts[1]);
    const year = Number(parts[2]);
    const parsed = new Date(year, month - 1, day);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  // This method is used to calculate membership expiry date using dc license issue date str, license expiry date str, and is free zone
  calculateMembershipExpiryDate(
    dcLicenseIssueDateStr: string,
    licenseExpiryDateStr: string,
    isFreeZone: boolean
  ): string {
    const parseDate = (dateStr: string): Date => {
      const [month, day, year] = dateStr.split("/").map(Number);
      return new Date(year, month - 1, day);
    };

    const formatDate = (date: Date): string => {
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    };
    let calculatedDate: any;
    const today = new Date();
    const dcLicenseIssueDate = parseDate(dcLicenseIssueDateStr);
    const licenseExpiryDate = parseDate(licenseExpiryDateStr);

    let data = this.form.getRawValue();
    let memExpiryDate = data.membershipExpiryDate.day ? `${data.membershipExpiryDate.month}/${data.membershipExpiryDate.day}/${data.membershipExpiryDate.year}` : '';

    //its freezone, use this logic IF(([Today] - [DC License Issue Date])>90, (Today()+365), [&LicenseExpiryDate])
    if (this.srSubType == ServiceName.MEMBERSHIP_NEW) {
      if (isFreeZone) {
        const daysSinceIssue = Math.floor(
          (today.getTime() - dcLicenseIssueDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysSinceIssue > 90) {
          // Return today + 365 days
          const todayDate = new Date(today);
          todayDate.setDate(todayDate.getDate() + 365);
          calculatedDate = formatDate(todayDate);
        } else {
          calculatedDate = formatDate(licenseExpiryDate);
        }
      } else {
        calculatedDate = formatDate(licenseExpiryDate);
      }
    } else {
      if (this.srSubType == ServiceName.MEMBERSHIP_RENEW) {
        let newExpriyDate: any = "";
        if (!isFreeZone) {
          newExpriyDate = licenseExpiryDate ? new Date(licenseExpiryDate) : new Date(memExpiryDate);
        } else {
          newExpriyDate = memExpiryDate ? new Date(memExpiryDate) : new Date(licenseExpiryDate);
        }
        calculatedDate = formatDate(newExpriyDate);
      } else {
        calculatedDate = memExpiryDate;
      }
    }
    return calculatedDate;
  }

  // This method is used to calculate form progress
  get formProgress() {
    if (this.form.disabled) {
      return 100;
    }
    if (this.step >= 5 && !this.form.invalid) {
      if (!this.checkRequiredDocuments()) {
        return 100;
      }
    }
    return Utils.calculateFormProgress(this.step, this.totalSteps)
  }

  //Unsave form confirmation
  // This method is used to determine if deactivate
  canDeactivate(): boolean {
    return this.sharedService.confirmNavigationIfFormDirty(this.form, this.isSaved);
  }

  // This method is used to close success modal
  closeSuccessModal() {
    this.successModal = false;
    sessionStorage.removeItem("srNumber");
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
  // This method is used to handel file event using action and document field
  handelFileEvent(action: any, documentField: any) {
    if (action == "c_sr") {
      if (this.srSubType == ServiceName.MEMBERSHIP_AMENDMENT || this.srSubType == ServiceName.MEMBERSHIP_RENEW && this.srNumber == "") {
        const formFieldData = this.form.getRawValue();
        this.submitSRData(formFieldData, SrActionType.DRAFT);
      }
    }
  }

  //Validate member license
  isTradeLicenseValid: boolean = false;
  licenseData: any = '';
  private readonly fieldsMaxLengths: Record<string, number> = {
    tradeNameEn: 150,
    tradeNameAr: 150,
    licenceNumber: 150,
    officeAddress: 30,
    buildingName: 30,
    street: 100,
    address: 200,
    legalName: 250,
    legalMemberNumber: 50,
    decreeNumber: 100,
    ownerName: 200,
    companyName: 200,
    passportNumber: 30
  };

  private limitVal(controlName: string, value: any): any {
    if (value == null || value == undefined) {
      return value;
    }

    const trimmedValue = value.toString().trim();
    const maxLength = this.fieldsMaxLengths[controlName];
    return maxLength ? trimmedValue.substring(0, maxLength) : trimmedValue;
  }

  private normalizeTradeLicenseField(value: any): string {
    return (value ?? '').toString().trim();
  }

  private getExistingLicenseMemberNo(): string {
    return this.normalizeTradeLicenseField(this.licenseData?.MemberNo);
  }

  private getExistingLicenseSrNumber(): string {
    const srFields = ['SRNumber'];
    for (const field of srFields) {
      const value = this.normalizeTradeLicenseField(this.licenseData?.[field]);
      if (value) {
        return value;
      }
    }
    return '';
  }

  get existingLicenseMessageKey(): string {
    if (this.getExistingLicenseMemberNo()) {
      return 'ALERT.LICENCE_ALREADY_EXIST';
    }
    if (this.getExistingLicenseSrNumber()) {
      return 'ALERT.LICENCE_SR_ALREADY_EXIST';
    }
    return 'TOAST_MESSAGES.TECHNICAL_ERROR';
  }

  get existingLicenseMessageParams(): Record<string, string> {
    if (this.existingLicenseMessageKey === 'ALERT.LICENCE_ALREADY_EXIST') {
      return {
        memberNo: this.getExistingLicenseMemberNo(),
        licenseNo: this.normalizeTradeLicenseField(this.licenseData?.LicenseNo),
      };
    }

    if (this.existingLicenseMessageKey === 'ALERT.LICENCE_SR_ALREADY_EXIST') {
      return {
        srNumber: this.getExistingLicenseSrNumber(),
      };
    }

    return {};
  }

  private hasTradeLicenseChangedFromInitialSr(): boolean {
    if (!this.initialTradeLicenseValues) {
      return true;
    }
    const currentValues = {
      issuingAuthority: this.normalizeTradeLicenseField(this.form.get('licenseIssuingAuthority')?.value),
      licenceType: this.normalizeTradeLicenseField(this.form.get('licenceType')?.value),
      licenceNumber: this.normalizeTradeLicenseField(this.form.get('licenceNumber')?.value),
    };
    return currentValues.issuingAuthority !== this.initialTradeLicenseValues.issuingAuthority
      || currentValues.licenceType !== this.initialTradeLicenseValues.licenceType
      || currentValues.licenceNumber !== this.initialTradeLicenseValues.licenceNumber;
  }

  // This method is used to validate trade license
  validateTradeLicense() {
    if (this.form.disabled) {
      this.isTradeLicenseValid = true;
      return;
    }
    this.isTradeLicenseValid = true;
    if (this.srSubType != ServiceName.MEMBERSHIP_NEW) {
      return;
    }
    //If sr inprogress/awaiting then return, no need to validate license again
    if (this.srInprgoressAwating) {
      return;
    }
    let issuingAuthority = this.form.get('licenseIssuingAuthority')?.value;
    let licenceType = this.form.get('licenceType')?.value;
    let licenceNumber = this.form.get('licenceNumber')?.value;
    if (this.isSrFromQueryParams && !this.hasTradeLicenseChangedFromInitialSr()) {
      return;
    }
    if (issuingAuthority == FieldValue.WITHOUT_LICENSE ||
      issuingAuthority == FieldValue.WITHOUT_LICENSE_AR) {
      return;
    }

    if (issuingAuthority && licenceNumber) {
      // Updated new api to display the error message for SR Number and Member Number
      const requestPayload = {
        "body": {
          "ProcessName": "DC Validate Member SR Info WF",
          "LicenseNo": licenceNumber,
          "LicenseAuth": issuingAuthority,
          "LicenseType": "",
          "MemberNo": ""
        }
      }
      this.api.post(
        ApiRoot.DCCICPIntegration,
        ApiEndPoints.RUN_PROCESS,
        //ApiEndPoints.VALIDATE_MEMBER_LICENSE,
        requestPayload
      ).subscribe({
        next: (response: any) => {
          if (response['Error Code'] == 0) {
            this.step = 1;
            this.isTradeLicenseValid = false;
            this.licenseData = response;
          } else {
            this.isTradeLicenseValid = true;
            this.getDETLicenseDetail();
          }
        },
        error: () => {
          this.isTradeLicenseValid = false;
        }
      })
    }
  }

  // For Renew and Amendment get member detail by csn
  selectedMemberExpiryDate: any;
  selectedMemberLicenseExpiryDate: any;
  get renewExpiryMinYear(): number | null {
    if (this.srSubType !== ServiceName.MEMBERSHIP_RENEW || !this.selectedMemberExpiryDate) {
      return null;
    }
    const date = this.selectedMemberExpiryDate instanceof Date
      ? this.selectedMemberExpiryDate
      : new Date(this.selectedMemberExpiryDate);
    return isNaN(date.getTime()) ? null : date.getFullYear() + 1;
  }
  // This method is used to get member detail
  getMemberDetail(updateExpiryDate: boolean = false) {
    if (!this.userCSN) {
      return;
    }
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Member Details Web WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": `${this.srSubType} Page`,
        "IOName": "DC Get Acc Details IO",
        "CSN": this.userCSN
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_MEMBER_DETAIL_BY_CSN, requestPayload)
      .subscribe({

        next: (response: any) => {
          if (response["Error Code"] == "" && response["Error Message"] == "") {
            this.isTradeLicenseValid = true;
            const serviceRequest = response.SiebelMessage?.["DC Account Data Transfer"];

            if (serviceRequest) {
              const expiryDate = serviceRequest["DC License Expiry Date"] != ''
                ? new Date(serviceRequest["DC License Expiry Date"])
                : null;

              const memExpiryDate = serviceRequest["DC Expiry Date"]
                ? new Date(serviceRequest["DC Expiry Date"])
                : new Date(serviceRequest["DC License Expiry Date"]);

              //update expiry date from member for renewal case
              if (updateExpiryDate) {
                this.selectedMemberExpiryDate = memExpiryDate;
                this.selectedMemberLicenseExpiryDate = expiryDate;
              } else {
                this.getMemberDocuments();
                //this.srAccountId = serviceRequest["Integration Id"] || serviceRequest["Parent Account Id"];
                this.licenseCategory = serviceRequest["DC License Category"];
                // Dates
                const issueDate = serviceRequest["DC License Issue Date"] != ''
                  ? new Date(serviceRequest["DC License Issue Date"])
                  : null;

                const decreeIssueDate = serviceRequest["DC Decree Issue Date"] != ''
                  ? new Date(serviceRequest["DC Decree Issue Date"])
                  : null;

                this.selectedMemberExpiryDate = memExpiryDate;
                this.selectedMemberLicenseExpiryDate = expiryDate;
                let cutAddress: any = "";
                if (serviceRequest["CUT Address"]) {
                  let memberAddress = Utils.checkIsArray(serviceRequest["CUT Address"]);
                  cutAddress = memberAddress ? memberAddress[0] : null;
                }

                const selectedCountry = this.getMatchedCountryValue(serviceRequest["DC Nationality"]) || "";

                this.form.patchValue({
                  licenseIssuingAuthority: serviceRequest["DC License Issue Authority"],
                  licenceType: serviceRequest["DC License Type"],
                  licenceNumber: this.limitVal('licenceNumber', serviceRequest["DC License Registration Number"]),
                  tradeNameEn: this.limitVal('tradeNameEn', serviceRequest["DC Name English"]),
                  tradeNameAr: this.limitVal('tradeNameAr', serviceRequest["DC Name Arabic"]),
                  issueDate: {
                    day: issueDate ? issueDate.getDate().toString().padStart(2, '0') : null,
                    month: issueDate ? (issueDate.getMonth() + 1).toString().padStart(2, '0') : null,
                    year: issueDate ? issueDate.getFullYear() : null,
                  },
                  expiryDate: {
                    day: expiryDate ? expiryDate.getDate().toString().padStart(2, '0') : null,
                    month: expiryDate ? (expiryDate.getMonth() + 1).toString().padStart(2, '0') : null,
                    year: expiryDate ? expiryDate.getFullYear() : null,
                  },
                  membershipExpiryDate: {
                    day: memExpiryDate ? memExpiryDate.getDate().toString().padStart(2, '0') : null,
                    month: memExpiryDate ? (memExpiryDate.getMonth() + 1).toString().padStart(2, '0') : null,
                    year: memExpiryDate ? memExpiryDate.getFullYear() : null,
                  },
                  nationality: selectedCountry ? selectedCountry : null,
                  legalStatus: serviceRequest["DC Legal Status"],
                  firmType: serviceRequest["Firm Type"],
                  officeAddress: this.limitVal('officeAddress', cutAddress ? cutAddress["Descriptor"] : ""),
                  buildingName: this.limitVal('buildingName', cutAddress ? cutAddress["Apartment Number"] : ""),
                  address: this.limitVal('address', cutAddress ? cutAddress["DC Address English Name"] : ""),
                  street: this.limitVal('street', cutAddress ? cutAddress["Street Address"] : ""),
                  countryCode: serviceRequest["DC Holder Phone"]
                    ? Utils.parseMobile(serviceRequest["DC Holder Phone"], 'cc')
                    : '971',
                  phoneNumber: serviceRequest["DC Holder Phone"]
                    ? Utils.parseMobile(serviceRequest["DC Holder Phone"], 'number')
                    : '',
                  contactEmail: serviceRequest["DC CRB Primary Email"],
                  hasParentCompany: serviceRequest["Parent Account Id"] && serviceRequest["Parent Account Lic Number"] ? "Yes" : "No",
                  parentCompanyLicenceNumber: serviceRequest["Parent Account Lic Number"],
                  parentCompanyMemberNumber: serviceRequest["Parent Account Number"],
                  membershipYear: null, //membershipYear
                  legalContactCountryCode: serviceRequest["DC Legal Contact"]
                    ? Utils.parseMobile(serviceRequest["DC Legal Contact"], 'cc')
                    : '971',
                  legalContactNumber: serviceRequest["DC Legal Contact"]
                    ? Utils.parseMobile(serviceRequest["DC Legal Contact"], 'number')
                    : '',
                  legalEmail: serviceRequest["DC Legal Email Addr"],
                  legalMemberNumber: this.limitVal('legalMemberNumber', serviceRequest["DC Legal Member Number"]),
                  legalName: this.limitVal('legalName', serviceRequest["DC Legal Name"]),
                  decreeNumber: this.limitVal('decreeNumber', serviceRequest["DC Decree Number"]),
                  decreeIssueDate: {
                    day: decreeIssueDate ? decreeIssueDate.getDate().toString().padStart(2, '0') : null,
                    month: decreeIssueDate ? (decreeIssueDate.getMonth() + 1).toString().padStart(2, '0') : null,
                    year: decreeIssueDate ? decreeIssueDate.getFullYear() : null,
                  },
                  primaryAddressId: cutAddress && cutAddress["Primary Address Id"] ? cutAddress["Primary Address Id"] : ''
                });
                this.onLicenseAuthorityAndTypeChagne(true);
                if (serviceRequest["Parent Account Id"]) {
                  this.licenseMember = {
                    'Error Code': "",
                    MemberName: serviceRequest["Parent DC Name English"], //Parent Company Name English
                    MemberNameAra: serviceRequest["Parent DC Name Arabic"],
                    ExpiryDate: serviceRequest["DC Parent License Expiry Date"]
                  }
                  this.isValidMemberNumber = true;
                }

                if (serviceRequest["DC License Issue Authority"] && serviceRequest["DC License Type"] && serviceRequest["DC License Issue Date"]) {
                  this.step = 2;
                }

                // --- Owners / Party Relationship ---
                if (serviceRequest["Party Relationship To"]) {
                  const relationships = Utils.checkIsArray(serviceRequest["Party Relationship To"]);


                  relationships?.map((p: any, index: number) => {
                    let relationshipKey = p["Relationship Type"] ? p["Relationship Type"] : "Owner";
                    const jobTitle = p["DC Job Title"] ? p["DC Job Title"] : "Owner";
                    const isAuthSignatory = p["DC Authorized Signatory"] ? p["DC Authorized Signatory"] : "N";
                    const role = this.getRolesFromRelationshipJobTitle(relationshipKey, jobTitle, isAuthSignatory);

                    const memberDob = p["Member DOB"] ? new Date(p["Member DOB"]) : null;
                    const passportExpDate = p["DC PP Exp Date"] ? new Date(p["DC PP Exp Date"]) : null;

                    const ownerCountry = this.getMatchedCountryValue(p["DC Cust Nationality"]) || "";

                    const object = {
                      ownerId: index + 1,
                      roles: role,
                      typeOfAddUser: "New profile",
                      partnerType: p["Related Party Type"] === "Organization" ? "Company" : "Individual",
                      isResident: p["UAE Citizen"] && (p["UAE Citizen"] == FieldValue.YES || p["UAE Citizen"] == FieldValue.YES_AR) ? "Yes" : 'No',
                      emiratesId: p["DC Emirates ID Number"],
                      emiratesDob: {
                        day: memberDob ? memberDob.getDate().toString().padStart(2, '0') : null,
                        month: memberDob ? (memberDob.getMonth() + 1).toString().padStart(2, '0') : null,
                        year: memberDob ? memberDob.getFullYear() : null,
                      },
                      passportNumber: this.limitVal('passportNumber', p["DC DED Partner Passport Number"]),
                      passportExpiry: {
                        day: passportExpDate ? passportExpDate.getDate().toString().padStart(2, '0') : null,
                        month: passportExpDate ? (passportExpDate.getMonth() + 1).toString().padStart(2, '0') : null,
                        year: passportExpDate ? passportExpDate.getFullYear() : null,
                      },
                      nameEn: this.limitVal('ownerName', p["Related Party Contact Name Eng"]),
                      nameAr: this.limitVal('ownerName', p["Related Party Contact Name Ara"]),
                      nationality: ownerCountry ? ownerCountry : null, //p["DC Cust Nationality"],
                      companyNameEn: this.limitVal('companyName', p["DC Name(ENGLISH)"]),
                      companyNameAr: this.limitVal('companyName', p["DC Name(ARABIC)"]),
                      share: p["DC Ownership Percent"],
                      mobile: p["DC Mobile Number"] ? Utils.parseMobile(p["DC Mobile Number"], 'number') : '',
                      countryCode: p["DC Mobile Number"] ? Utils.parseMobile(p["DC Mobile Number"], 'cc') : '',
                      email: p["DC Email Address"],
                      accountMembershipId: p["Row Id"] ? p["Row Id"] : '',
                      endDate: p["DC End Date"] ? p["DC End Date"] : ''
                    };

                    const existingIndex = this.ownerList.findIndex((o: any) => o.ownerId === object.ownerId);
                    if (this.ownerList.length > 0 && existingIndex !== -1) {
                      this.ownerList[existingIndex].roles.push(role);
                    } else {
                      this.ownerList.push(object);
                    }
                  });

                  if (this.ownerList.length > 0) {
                    this.updateOwnersControlState();
                    this.showShareMsg =
                      this.ownerTotalShare != null &&
                      (this.ownerTotalShare < 100 || this.ownerTotalShare > 100);
                    this.step = 3;
                  }
                }

                //Activities
                if (serviceRequest["DC Account Activity"]) {
                  let licenseActivities: any = [];
                  let activities = Utils.checkIsArray(serviceRequest["DC Account Activity"]);
                  activities?.map((activity: any) => {
                    if (activity["DC Activity Code"] &&
                      (
                        activity["DC Ref Description"] || activity["DC Ref Description Arabic"] ||
                        activity["DC Special Activity Code Description (English)"] || activity["DC Special Activity Code Description (Arabic)"]
                      )
                    ) {
                      let activityObj = {
                        codeFee: activity["DC Activity Fee"],
                        description: activity["DC Ref Description Arabic"],
                        code: activity["DC Activity Code"],
                        name: activity["DC Ref Description"],
                        descriptionOtherEn: activity["DC Special Activity Code Description (English)"],
                        descriptionOtherAr: activity["DC Special Activity Code Description (Arabic)"],
                        accountActivityId: activity["DC Account Activity Id"] ? activity["DC Account Activity Id"] : '',
                        endDate: activity["DC End Date"] ? activity["DC End Date"] : ''
                      }
                      licenseActivities.push(activityObj);
                    }
                  })
                  this.addAactivity(licenseActivities);
                  if (licenseActivities) {
                    this.form.get('activitiesConfirmed')?.setValue('Yes');
                  }
                  this.step = 4;
                }

                if (serviceRequest["DC Account Activity"]) {
                  this.step = 4;
                }

                this.form.get('expiryDate.year')?.disable();

                if (this.srSubType == ServiceName.MEMBERSHIP_RENEW &&
                  ((this.selectedLicenseAuthority &&
                    this.selectedLicenseAuthority["Low"] == "Freezone") ||
                    this.licenseCategory == "Freezone")
                ) {
                  this.form.get('expiryDate.year')?.disable();
                  this.toggleRequired(true, ['membershipYear']);
                } else {
                  if (this.srSubType == ServiceName.MEMBERSHIP_RENEW) {
                    this.form.get('expiryDate.year')?.enable();
                  }
                }

                if (issueDate == null) {
                  this.form.get('issueDate')?.enable();
                }
                if (expiryDate == null) {
                  this.form.get('expiryDate')?.enable();
                }

                if (this.srSubType == ServiceName.MEMBERSHIP_AMENDMENT || this.srSubType == ServiceName.MEMBERSHIP_RENEW && !this.srNumber) {
                  const formFieldData = this.form.getRawValue();
                  this.submitSRData(formFieldData, SrActionType.DRAFT);
                }

              }
            }
          } else {
            this.technicalErrorMsg = true;
          }
        },
        // This method is used to handle the error callback
        error: () => {
        }
      })
  }
  // Merge member wallet documents into current SR folder for missing allowed types
  private mergeMemberDocumentsIntoSrFolder(): void {
    if (!this.memberDocuments?.length || !this.srFolder?.allowedDocumentTypes?.length) {
      return;
    }

    const cleared = MembershipSharedService.applyPendingMoaClear(
      this.memberDocuments,
      this.shouldClearMoaFromMemberDocs
    );
    this.memberDocuments = cleared.memberDocuments;
    this.shouldClearMoaFromMemberDocs = cleared.shouldClearMoaFromMemberDocs;

    const allowedTypes = Array.from(
      new Set(this.srFolder.allowedDocumentTypes.map((d: any) => d.nameEn))
    );
    const existingTypes = new Set(
      (this.srFolder.documents ?? []).map((doc: any) => doc.documentType)
    );
    const missingTypes = allowedTypes.filter(type => !existingTypes.has(type));

    if (missingTypes.length === 0) {
      return;
    }

    const docsToMerge = this.memberDocuments
      .filter((doc: any) => missingTypes.includes(doc.documentType))
      .filter((doc: any) => doc.documentType !== 'Trade_License_Copy')
      .map((doc: any) => ({
        ...doc,
        memberFolderId: this.memberDocumentFolderId ?? null,
      }));

    if (docsToMerge.length > 0) {
      this.srFolder = {
        ...(this.srFolder ?? {}),
        documents: [
          ...(this.srFolder.documents ?? []),
          ...docsToMerge,
        ],
      };
    }
  }
  // This method is used to get member documents
  getMemberDocuments() {
    if (!this.userCSN) {
      return
    }
    if (this.memberDocumentsFetched) {
      this.mergeMemberDocumentsIntoSrFolder();
      return;
    }
    this.memberDocumentsFetched = true;
    let documentType = DocumentServiceType.MEMBERSHIP_WALLET;
    this.sharedService
      .getDocuments('', documentType, false, {
        memNumber: this.userCSN,
      })
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            const memberDocuments = response.data ?? response;
            this.memberDocumentFolderId = memberDocuments?.folderId ?? null;
            const fetchedDocs = memberDocuments?.documents ?? [];
            const cleared = MembershipSharedService.applyPendingMoaClear(
              fetchedDocs,
              this.shouldClearMoaFromMemberDocs
            );
            this.memberDocuments = cleared.memberDocuments;
            this.shouldClearMoaFromMemberDocs = cleared.shouldClearMoaFromMemberDocs;
            this.mergeMemberDocumentsIntoSrFolder();
          } else {
            this.memberDocumentsFetched = false;
          }
        },
        error: () => {
          this.memberDocumentsFetched = false;
        },
      });
  }
  // This method is used to close pending sr modal
  closependingSrModal() {
    this.pendingSrNumber = "";
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
    sessionStorage.removeItem("srNumber");
  }
  onPaymentMethodChange(value: any) {
    this.noAdvancePaymentBalance = false;
    const selectedValue = value;
    if (selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES || selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES_AR) {
      this.noAdvancePaymentBalance = !this.sharedService.hasSufficientAdvanceForServicesBalance();
    } else {
      this.noAdvancePaymentBalance = false;
    }
  }

  getActivitiesToSubmit(): any {
    if (this.srSubType == ServiceName.MEMBERSHIP_NEW) {
      return this.selectedActivities;
    }
    // Take only master activities with endDate
    const removedActivities = this.masterActivities.filter(
      (activity: any) => !!activity.endDate
    ) || [];
    // Take all selected activities
    const activeActivities = Array.isArray(this.selectedActivities) ? [...this.selectedActivities] : [];
    // Merge both arrays
    const combinedActivities = [...activeActivities, ...removedActivities];
    return combinedActivities;
  }
  getOwnerPartnerToSubmit(activeOwners: any) {
    if (this.srSubType == ServiceName.MEMBERSHIP_NEW) {
      return activeOwners || [];
    }
    const removedOwners = [
      ...(this.masterOwnerList?.filter((owner: any) => !!owner.endDate) || []),
      ...(this.ownerList?.filter((owner: any) => !!owner.endDate) || [])
    ];
    const uniqueRemovedOwners = removedOwners.filter(
      (owner: any, index: number, self: any[]) =>
        self.findIndex((o: any) => o.ownerId === owner.ownerId) === index
    );

    const activeOwnerList = Array.isArray(activeOwners) ? [...activeOwners] : [];
    const combinedOwners = [...activeOwnerList, ...uniqueRemovedOwners];

    return combinedOwners;
  }

  countryLovList: any = [];
  private getMatchedCountryValue(countryName: string): string | null {
    const normalizedCountryName = (countryName || '').trim().toLowerCase();
    if (!normalizedCountryName || !Array.isArray(this.countryLovList)) {
      return null;
    }
    const matchedCountry = this.countryLovList.find((country: any) => {
      const normalizedName = (country?.Name || '').trim().toLowerCase();
      const normalizedValue = (country?.Value || '').trim().toLowerCase();

      return normalizedName === normalizedCountryName || normalizedValue === normalizedCountryName;
    });

    return matchedCountry?.Value || null;
  }

  getCountryLOV() {
    this.srDetailLoading = true;
    this.lookupService.post(LookupKey.LOOKUP_URL, {
      "ProcessName": "DC Get List Of Values WF",
      "Type": LookupKey.COUNTRY,
      "LanguageCode": this.lang == 'ar' ? 'ARA' : 'ENU',
      "ParentValue": "",
      "Sub Type": ""
    })
      .subscribe({
        next: (response: any) => {
          if (response['Error Message'] == null && response.SiebelMessage?.['List Of Values']) {
            this.countryLovList = Utils.checkIsArray(response.SiebelMessage['List Of Values']);
            this.checkForPendingSr();
            this.openFolder();
          }
        }
      })

  }

  showStep2DraftConfirmModal: boolean = false;
  pendingStep2DraftFormData: any = null;

  private lockTradeLicenseFieldsForNewMembership(): void {
    if (this.srSubType !== ServiceName.MEMBERSHIP_NEW || this.type !== 'registration' || !this.srNumber) {
      return;
    }

    ['licenseIssuingAuthority', 'licenceType', 'licenceNumber'].forEach((controlName: string) => {
      const control = this.form.get(controlName);
      const value = control?.value;
      const hasValue = value !== null && value !== undefined && String(value).trim() !== '';

      if (hasValue) {
        control?.disable({ emitEvent: false });
      } else {
        control?.enable({ emitEvent: false });
      }
    });
  }

  private shouldShowStep2DraftConfirmModal(): boolean {
    return this.step === 2 && this.srSubType === ServiceName.MEMBERSHIP_NEW && this.type === 'registration' && !this.srNumber;
  }
  isOpenFromSaveAndClose: boolean = false;
  confirmStep2DraftSave(): void {
    if (!this.pendingStep2DraftFormData) {
      this.showStep2DraftConfirmModal = false;
      return;
    }

    this.showStep2DraftConfirmModal = false;
    this.submitSRData(this.pendingStep2DraftFormData, this.isOpenFromSaveAndClose ? SrActionType.SAVE : SrActionType.DRAFT);
    this.pendingStep2DraftFormData = null;
    this.isOpenFromSaveAndClose = false;
    this.lockTradeLicenseFieldsForNewMembership();

    if (this.step < 5) {
      if (this.step >= 2 && this.isOffshoreLicenseType) {
        this.step = 5;
      } else {
        this.step++;
      }
    }
  }

  cancelStep2DraftSave(): void {
    this.showStep2DraftConfirmModal = false;
    this.pendingStep2DraftFormData = null;
    this.isOpenFromSaveAndClose = false;
  }

}
